// Correction-form rendering + submission.
//
// Every renderable row gets a horizontal strip of buttons (one per kind it
// supports). Clicking a button reveals an inline form whose fields depend
// on the kind. Submit POSTs the event to the Worker and toggles the row
// into a "submitted" state — no client-side state graph beyond that.

import { ApiError, deleteJson, postJson } from "./api";
import { inFixtureMode } from "./fixture";
import { OPTIMISTIC_STATE_KINDS } from "./optimistic_feedback";
import { gateHintEvents } from "./session_events";

// #74a: the wire kinds the Worker gates against the recipient's own run events
// (the close gates + the reverse blocker-adding/placement guards — see
// worker/src/routes/data.ts). A submit of one of these carries `client_events`
// so the gate replays a just-POSTed move-out that Cloudflare KV's eventually-
// consistent list() may not have indexed yet (the emptied-sub-category close
// blocker). Mirrors the worker's BLOCKER_ADDING_KINDS ∪ PLACEMENT_KINDS ∪ the
// three close kinds; over-inclusion is harmless (the Worker ignores the hint
// for non-gated kinds), under-inclusion would silently re-open the race.
const GATE_HINT_KINDS: ReadonlySet<string> = new Set([
  "thread_close",
  "work_item_close",
  "subcategory_close",
  "action_reopen",
  "work_item_reopen",
  "action_move_to_item",
  "item_move_to_category",
  "item_move_to_subcategory",
  "place_as_action",
  "place_from_unfiled",
]);

// Mirrors agent/config.py::slugify so the SPA can derive a thread slug
// from a recipient-typed display name without an extra round-trip.
// Idempotent. Empty input → "thread" (matches the agent fallback).
export function slugify(name: string): string {
  const s = name.toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return s || "thread";
}

// Pull the thread slug out of the SPA's thread-header targetId, which
// is encoded as "thread:<slug>" by target_id.ts::threadKey. Mirrors the
// absorber's _parse_thread_slug_from_target so the same input produces
// the same slug on both sides.
function threadSlugFromTargetId(targetId: string): string {
  if (targetId.startsWith("thread:")) {
    return targetId.slice("thread:".length).split("/")[0] ?? "";
  }
  return targetId;
}

// Reshape the raw form payload into the shape the agent absorber expects.
// Pure function so it's unit-testable without mocking the DOM / fetch.
// Returns a fresh object; does not mutate the input.
// The 4 recipient-settable priorities (Informational is synth-only — see
// FormControlSpec.priorityStatus). `Active` first so it's the default
// selection. Shared by the action_add / place_from_unfiled / place_as_action
// priority dropdowns.
const SETTABLE_PRIORITY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "Active", label: "Active" },
  { value: "Critical", label: "Critical" },
  { value: "Delay Potential", label: "Delay Potential" },
  { value: "On Hold", label: "On Hold" },
];

export function reshapePayloadForKind(
  kind: string,
  payload: Record<string, unknown>,
  targetId: string,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...payload };

  // thread_reject: form collects evidence_item_ids as a comma-separated
  // string; the absorber expects a list. Empty input → drop the key so
  // the absorber treats it as a project-level note with no per-item
  // reclassify. (Slice 8 Wave 3.)
  if (kind === "thread_reject") {
    const raw = typeof out.evidence_item_ids === "string"
      ? out.evidence_item_ids
      : "";
    const ids = raw.split(",").map((s) => s.trim()).filter((s) => s);
    if (ids.length) out.evidence_item_ids = ids;
    else delete out.evidence_item_ids;
  }

  // responsible_party_assign: empty email string → drop the key.
  // Absorber expects optional string, not empty string. (Slice 8 Wave 3.)
  // is_new_person: FormData only includes checkboxes when checked, so
  // presence of any value → true, absence → false. Absorber defaults
  // is_new_person to False already, but emitting it explicitly keeps
  // the wire payload faithful to the form state.
  if (kind === "responsible_party_assign") {
    if (typeof out.email === "string" && !out.email.trim()) {
      delete out.email;
    }
    out.is_new_person = "is_new_person" in payload;
  }

  // thread_rename: form collects { new_name }; absorber requires
  // { from_slug, to_name }. Source slug comes from the thread-header
  // row's targetId. Rename is name-only under the slug-decouple model —
  // the slug stays put as the stable identifier; slug-changing
  // operations go through thread_merge.
  if (kind === "thread_rename" && typeof out.new_name === "string") {
    out.from_slug = threadSlugFromTargetId(targetId);
    out.to_name = (out.new_name as string).trim();
    delete out.new_name;
  }

  // thread_merge: form collects { merge_into } (the operator picks the
  // destination by visible name via datalist; the input value is the
  // slug); absorber requires { source_slugs[], target_slug }. Source
  // slug comes from the thread-header row's targetId. Slugify-fallback
  // catches the case where the operator typed a human name that the
  // datalist didn't intercept (e.g., mobile browser without datalist
  // support, or a typo that didn't match an option).
  if (kind === "thread_merge" && typeof out.merge_into === "string") {
    out.source_slugs = [threadSlugFromTargetId(targetId)];
    out.target_slug = ensureSlug(out.merge_into as string);
    delete out.merge_into;
  }

  // thread_retag: form collects { to_thread } from a thread datalist.
  // Same slugify-fallback as thread_merge for mobile / typo cases.
  // Absorber already requires `to_thread` (no key remap needed).
  if (kind === "thread_retag" && typeof out.to_thread === "string") {
    out.to_thread = ensureSlug(out.to_thread as string);
  }

  // --- v4 (Task #9) reshapes -----------------------------------------
  //
  // The form's <input> elements all yield strings; the agent absorber
  // expects typed values for specific fields. Drop-empty + coerce
  // here so the wire payload matches what the Pydantic models accept.

  // action_open_question_resolve (#52 -> #52b): the picker's selected value is
  // prefixed so we can route it to the right absorber key — `qid:<hex>` for a
  // question carrying a stable identity (the preferred key, survives a synth
  // reword), or `text:<text>` for a legacy question that has none yet. A raw
  // free-text fallback (no prefix) stays `question_text`.
  if (kind === "action_open_question_resolve"
      && typeof out.question_text === "string") {
    const ref = out.question_text;
    if (ref.startsWith("qid:")) {
      out.qid = ref.slice(4);
      delete out.question_text;
    } else if (ref.startsWith("text:")) {
      out.question_text = ref.slice(5);
    }
  }

  // invoice_add: amount comes as string from the text input; absorber
  // coerces but rejects on parse failure. Drop empty strings so the
  // absorber treats amount as absent rather than failing to coerce
  // "".
  if (kind === "invoice_add"
      && typeof out.amount === "string"
      && !out.amount.trim()) {
    delete out.amount;
  }

  // action_set_due / action_add / action_priority_change: empty due
  // string → drop (clearing due is signaled with `null`, not `""`).
  // Decision 3C added `due` to action_priority_change for the
  // combined form path.
  if ((kind === "action_set_due"
        || kind === "action_add"
        || kind === "action_priority_change")
      && typeof out.due === "string"
      && !out.due.trim()) {
    out.due = null;
  }

  // A³/A² (2026-06-03): place_from_unfiled is now the **Place as Item**
  // tier only (create a new Item + required first Action). Derive the
  // Item slug from the name; slugify the Category picker value (datalist
  // gives a slug, ensureSlug tolerates typed names). source_ref +
  // unfiled_id are injected by the submit handler from spec.bucketContext.
  if (kind === "place_from_unfiled") {
    delete out.mode;
    delete out.target_work_item_slug;  // legacy place-under-existing mode → place_as_action
    if (typeof out.new_item_name === "string"
        && (out.new_item_name as string).trim()) {
      out.new_item_work_item_slug = slugify((out.new_item_name as string).trim());
    }
    if (typeof out.new_item_thread_slug === "string") {
      out.new_item_thread_slug = ensureSlug(out.new_item_thread_slug as string);
    }
    for (const k of [
      "new_item_thread_slug", "new_item_subcategory_slug",
      "new_item_work_item_slug",
      "new_item_name", "new_item_narrative", "new_item_action_text",
    ]) {
      if (typeof out[k] === "string" && !(out[k] as string).trim()) {
        delete out[k];
      }
    }
  }

  // A³/A² Place as Category: derive the Category slug from the name.
  if (kind === "place_as_category" && typeof out.name === "string"
      && (out.name as string).trim()) {
    out.thread_slug = slugify((out.name as string).trim());
  }

  // A³/A² Place as Action: derive the Action slug from the text; slugify
  // the target Item picker value (datalist → slug; ensureSlug tolerates names).
  if (kind === "place_as_action") {
    if (typeof out.text === "string" && (out.text as string).trim()) {
      out.action_item_slug = slugify((out.text as string).trim());
    }
    if (typeof out.target_work_item_slug === "string") {
      out.target_work_item_slug = ensureSlug(out.target_work_item_slug as string);
    }
  }

  // #60 PR4-v2: the Place tiers carry a `complete` checkbox (born-closed when
  // checked). FormData only includes a checkbox when checked → presence=true.
  if (kind === "place_as_category" || kind === "place_from_unfiled"
      || kind === "place_as_action") {
    out.complete = "complete" in payload;
  }

  // B8: slugify the destination Item value (datalist gives a slug; ensureSlug
  // tolerates a typed name).
  if (kind === "action_move_to_item"
      && typeof out.to_work_item_slug === "string") {
    out.to_work_item_slug = ensureSlug(out.to_work_item_slug as string);
  }

  // #60 PR4-v2 merge_into_existing: keep only the active tier's slug fields
  // (hidden rows already cleared; this prunes empties + the resolved action
  // pick helper), with ensureSlug fallback for free-typed values.
  if (kind === "merge_into_existing") {
    delete out.target_action_pick;  // resolved into the two slugs pre-reshape
    delete out.merge_scope;          // UI-only toggle, not a wire field
    for (const k of [
      "target_thread_slug", "target_work_item_slug", "target_action_item_slug",
    ]) {
      if (typeof out[k] !== "string") continue;
      const v = (out[k] as string).trim();
      if (!v) { delete out[k]; continue; }
      out[k] = ensureSlug(v);
    }
    if (typeof out.note === "string" && !(out.note as string).trim()) {
      delete out.note;
    }
  }

  // Decision 7B: silent slug derivation for the three v4 "Add"
  // affordances. The forms no longer surface a slug field; we
  // compute it from the human-facing name/text on submit.
  if (kind === "item_add" && typeof out.name === "string"
      && (out.name as string).trim()) {
    out.work_item_slug = slugify((out.name as string).trim());
  }
  if (kind === "category_add" && typeof out.name === "string"
      && (out.name as string).trim()) {
    out.thread_slug = slugify((out.name as string).trim());
  }
  if (kind === "action_add" && typeof out.text === "string"
      && (out.text as string).trim()) {
    out.action_item_slug = slugify((out.text as string).trim());
  }

  // Decision 7A: item_move_to_category — the datalist value IS the
  // slug, but slugify any free-text typo so the absorber gets a
  // canonical form.
  if (kind === "item_move_to_category"
      && typeof out.to_thread_slug === "string") {
    out.to_thread_slug = ensureSlug(out.to_thread_slug as string);
  }
  // #76: action_to_item — derive the new Item's candidate slug from its name
  // (the absorber uniquifies it) + canonicalize the destination Category slug
  // (the <select> value is already a slug; ensureSlug guards a stray typo).
  if (kind === "action_to_item") {
    if (typeof out.name === "string") {
      out.name = (out.name as string).trim();
      out.new_work_item_slug = ensureSlug(out.name as string);
    }
    if (typeof out.to_thread_slug === "string") {
      out.to_thread_slug = ensureSlug(out.to_thread_slug as string);
    }
    // `a` (Ray 2026-07-22): canonicalize the optional Sub-category pick; drop
    // "(Ungrouped)" ("") so the wire carries a slug only when a real group was
    // chosen (the absorber reads new_item_subcategory_slug; a missing/empty value
    // files the promoted Item directly under the Category).
    if (typeof out.new_item_subcategory_slug === "string") {
      const sc = (out.new_item_subcategory_slug as string).trim();
      if (sc) out.new_item_subcategory_slug = ensureSlug(sc);
      else delete out.new_item_subcategory_slug;
    }
  }
  // B10: `also_close` is a UI-only flag (the submit handler fires a separate
  // work_item_close); it never belongs in the move payload.
  if (kind === "item_move_to_category") {
    delete out.also_close;
  }

  // Task 3: candidate_to_item — derive work_item_slug from the name
  // (client never types a slug), slugify the Category picker value, and
  // drop the first-action priority when no action text was entered.
  if (kind === "candidate_to_item") {
    if (typeof out.name === "string" && (out.name as string).trim()) {
      out.work_item_slug = slugify((out.name as string).trim());
    }
    if (typeof out.target_thread_slug === "string") {
      out.target_thread_slug = ensureSlug(out.target_thread_slug as string);
    }
    if (!(typeof out.initial_action_text === "string"
          && (out.initial_action_text as string).trim())) {
      delete out.initial_action_text;
      delete out.initial_action_priority;
    }
  }

  // Task 3: work_item_merge — normalize the multi-selected source slugs
  // (ensureSlug each, drop empties). Accept a bare string as well as a list:
  // the checkbox-list multiselect yields a single string for one selection,
  // and the absorber requires a list — coercing here (defense in depth with
  // the submit-path array re-collection) stops single-source merges dropping.
  if (kind === "work_item_merge" && out.source_work_item_slugs != null) {
    out.source_work_item_slugs = coerceSlugList(out.source_work_item_slugs);
  }

  // Open improvement #3: action_merge — normalize source action slugs.
  if (kind === "action_merge" && out.source_action_item_slugs != null) {
    out.source_action_item_slugs = coerceSlugList(out.source_action_item_slugs);
  }

  // --- #38 sub-category reshapes -------------------------------------------
  // subcategory_add: the submit handler mints `subcategory_slug` (collision-
  // suffixed against the parent's groups + session-pending adds, via
  // mintSubcategorySlug). Derive a plain slugify fallback here so a direct
  // reshape call (unit tests / no-spec path) still yields a slug; trim name.
  if (kind === "subcategory_add") {
    if (typeof out.name === "string") out.name = (out.name as string).trim();
    const minted = typeof out.subcategory_slug === "string"
      && (out.subcategory_slug as string).trim();
    if (!minted && typeof out.name === "string" && out.name) {
      out.subcategory_slug = slugify(out.name as string);
    }
  }
  // subcategory_rename: trim the new display name (slug is stable, parsed from
  // the target id absorber-side — no slug field on the wire).
  if (kind === "subcategory_rename" && typeof out.to_name === "string") {
    out.to_name = (out.to_name as string).trim();
  }
  // item_move_to_subcategory: the <select> submits the destination group slug,
  // or "" for the explicit "(ungrouped)" choice → null (the absorber's ungroup
  // sentinel). A real slug is ensureSlug'd for canonical form.
  if (kind === "item_move_to_subcategory"
      && typeof out.to_subcategory_slug === "string") {
    const trimmed = (out.to_subcategory_slug as string).trim();
    out.to_subcategory_slug = trimmed ? ensureSlug(trimmed) : null;
  }

  // Universal v4 hygiene: trim empty optionals on these kinds so the
  // absorber's `if not value` checks don't see whitespace.
  const trimEmpty: Record<string, string[]> = {
    item_add: [
      "narrative", "responsible_party_name",
      "responsible_party_email", "start_date",
    ],
    category_add: ["description"],
    action_add: [
      "narrative", "reason", "impact",
      "off_hold_condition", "assignee", "due",
    ],
    // Decision 3C: combined form may submit conditional fields;
    // trim empties so the absorber's optional-field checks behave.
    action_priority_change: ["reason", "impact", "off_hold_condition"],
    // Decision 7B: silent slugs — narrative/responsible/etc. trim.
    place_from_unfiled: ["new_item_narrative"],
    // A³/A² (2026-06-03): Place-as-Category optional description.
    place_as_category: ["description"],
    invoice_add: ["received_date", "due_date", "blocking_note", "status"],
    invoice_blocking_note_set: ["blocking_note"],
    action_open_question_add: ["raised_by"],
    // Task 3
    candidate_to_item: ["narrative", "initial_action_text"],
    work_item_merge: ["target_name"],
    // Open improvement #3
    action_merge: ["target_text"],
    // #38: drop an empty close/reopen reason (absent → None absorber-side).
    subcategory_close: ["reason"],
    subcategory_reopen: ["reason"],
    // #74b: delete carries an optional reason; drop it when blank.
    subcategory_delete: ["reason"],
  };
  const trimKeys = trimEmpty[kind];
  if (trimKeys) {
    for (const k of trimKeys) {
      if (typeof out[k] === "string" && !(out[k] as string).trim()) {
        delete out[k];
      }
    }
  }

  return out;
}

// Decision 3B (close-out 2026-05-25): kind→field map for "Add X" vs
// "Edit X" label switching. Keyed on the metadata-edit kinds where
// the populated state is meaningful; everything else uses KIND_LABEL.
const ADD_EDIT_FIELD_BY_KIND: Record<string, string> = {
  action_set_reason: "reason",
  action_set_impact: "impact",
  action_set_off_hold_condition: "off_hold_condition",
  action_set_due: "due",
};

function labelForKind(
  kind: string,
  populated?: ReadonlySet<string>,
): string {
  const field = ADD_EDIT_FIELD_BY_KIND[kind];
  if (!field) return KIND_LABEL[kind] ?? kind;
  const isSet = populated?.has(field) ?? false;
  const verb = isSet ? "Edit" : "Add";
  const noun = field === "off_hold_condition"
    ? "off-hold condition"
    : field === "due"
      ? "due date"
      : field;
  return `${verb} ${noun}`;
}

// Slugify-on-submit safety net: when the operator types a free-form name
// that didn't match a datalist option, fall back to slugify() so the
// resulting slug at least has a chance of matching agent-side. Already-
// well-formed slugs pass through unchanged.
function ensureSlug(value: string): string {
  const trimmed = value.trim();
  if (/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(trimmed)) return trimmed;
  return slugify(trimmed);
}

/** Coerce a merge form's source-slugs field to a clean slug array, accepting
 * a bare string (single checkbox selection) or a list. Empties/non-strings
 * dropped, each ensureSlug'd. Mirrors the absorber's `_coerce_slug_list` so
 * the SPA→absorber contract holds even if one side regresses. */
function coerceSlugList(raw: unknown): string[] {
  const items = Array.isArray(raw) ? raw : [raw];
  return items
    .filter((s): s is string => typeof s === "string" && s.trim().length > 0)
    .map((s) => ensureSlug(s.trim()));
}

/** #38: mint a collision-free sub-category slug from a recipient-typed name.
 * slugify, then suffix `-2`, `-3`, … against the parent Category's existing
 * group slugs. The `existing` set MUST span OPEN *and* CLOSED groups (the
 * per-(project, thread) UNIQUE space spans both, and the absorber's
 * idempotent name-refresh on a colliding slug would silently rename the wrong
 * group instead of creating a new one) AND this session's pending adds (else
 * two same-named adds in one sitting collapse via that same name-refresh
 * branch — the binding amendment). Pure → unit-testable. */
export function mintSubcategorySlug(
  name: string,
  existing: Iterable<string>,
): string {
  const base = slugify(name);
  const taken = new Set(existing);
  if (!taken.has(base)) return base;
  for (let i = 2; ; i += 1) {
    const candidate = `${base}-${i}`;
    if (!taken.has(candidate)) return candidate;
  }
}

/** #38 (operator feedback 2026-06-12): mint BOTH the collision-free slug and a
 * disambiguated DISPLAY name. When the slug had to be suffixed (`-2`, `-3`, …),
 * mirror the same number into the visible name ("Test Group" → "Test Group 2")
 * so two same-named groups in one session are visually distinct, not just
 * slug-distinct. No collision → name unchanged. Pure → unit-testable. */
export function mintSubcategory(
  name: string,
  existing: Iterable<string>,
): { slug: string; name: string } {
  const trimmed = name.trim();
  const base = slugify(trimmed);
  const slug = mintSubcategorySlug(trimmed, existing);
  if (slug === base) return { slug, name: trimmed };
  const suffix = slug.slice(base.length + 1);   // the `-N` tail → "N"
  return {
    slug,
    name: /^\d+$/.test(suffix) ? `${trimmed} ${suffix}` : trimmed,
  };
}

/** #63: the human label shown for an Item in a move/place picker —
 * "N.M Item name (Category)". Used BOTH to build the picker option and to map
 * the picked label back to its slug at submit, so the two never diverge. */
export function itemOptionLabel(i: ItemOption): string {
  return [
    i.display_number ? `${i.display_number}` : null,
    i.name,
    i.thread_name ? `(${i.thread_name})` : null,
  ].filter(Boolean).join(" ");
}

/** #63 follow-up (2026-06-09): the human label for a Category in a move/merge
 * picker — "N Category name" (the number when present; client asked for it).
 * Shared by the picker render and the submit-time slug resolution. */
export function threadOptionLabel(t: ThreadOption): string {
  return t.display_number ? `${t.display_number} ${t.name}` : t.name;
}

/** #63: the move/merge target pickers display the friendly Category name /
 * "N.M Item (Category)" label (the slug is a technical artifact the client
 * never needs to see). Resolve a picked label back to the STABLE slug here,
 * via the picker's own options — NOT slugify: a renamed Category keeps its
 * original slug, so `slug !== slugify(name)`. Mutates `raw` in place; a typed
 * value matching no option is left untouched so reshape's ensureSlug() still
 * handles the free-text path. Pure → unit-testable. */
export function resolvePickedTargetSlugs(
  raw: Record<string, unknown>,
  threadOptions: ThreadOption[] = [],
  itemOptions: ItemOption[] = [],
): void {
  const threadBySlug = new Set(threadOptions.map((t) => t.slug));
  // Accept either the full picker label ("N name") or the bare name (a user
  // who types just the name); both resolve to the stable slug.
  const threadByKey = new Map<string, string>();
  for (const t of threadOptions) {
    threadByKey.set(threadOptionLabel(t), t.slug);
    if (!threadByKey.has(t.name)) threadByKey.set(t.name, t.slug);
  }
  for (const f of [
    "merge_into", "to_thread_slug", "target_thread_slug", "new_item_thread_slug",
  ]) {
    const v = raw[f];
    if (typeof v !== "string") continue;
    if (threadBySlug.has(v)) continue;        // already a slug — leave it
    if (threadByKey.has(v)) raw[f] = threadByKey.get(v);
  }
  const itemBySlug = new Set(itemOptions.map((i) => i.slug));
  const itemByLabel = new Map(itemOptions.map((i) => [itemOptionLabel(i), i.slug]));
  for (const f of ["target_work_item_slug", "to_work_item_slug"]) {
    const v = raw[f];
    if (typeof v !== "string") continue;
    // Already a known slug (defensive — e.g. a programmatic value): leave it.
    if (itemBySlug.has(v)) continue;
    if (itemByLabel.has(v)) raw[f] = itemByLabel.get(v);
  }
}

/** #60 PR4-v2: resolve the place_new pseudo-kind to its real wire kind by the
 * `place_tier` select. Returns the wire kind; the per-tier raw fields are
 * already named to match that kind's reshape. Defaults to place_from_unfiled
 * (Item) — the most common pick — if the tier is somehow unset. */
export function resolvePlaceNewKind(raw: Record<string, unknown>): FeedbackKind {
  const tier = raw.place_tier;
  delete raw.place_tier;  // not a wire field
  if (tier === "category") return "place_as_category";
  if (tier === "action") return "place_as_action";
  return "place_from_unfiled";
}

/** #38 feedback 2.1: resolve the item_move pseudo-kind to its real wire kind
 * by the `move_tier` select, pruning the inactive tier's fields (a hidden
 * <select> still submits via FormData, and reshape copies every key — without
 * the prune a stray to_subcategory_slug would ride the Category-move wire
 * payload). Defaults to the Category tier if the select is somehow unset.
 * Mutates `raw` in place; pure → unit-testable. */
export function resolveItemMoveKind(raw: Record<string, unknown>): FeedbackKind {
  const tier = raw.move_tier;
  delete raw.move_tier;  // not a wire field
  if (tier === "subcategory") {
    delete raw.to_thread_slug;
    delete raw.also_close;   // B10 Move & Close is a Category-move concept
    return "item_move_to_subcategory";
  }
  delete raw.to_subcategory_slug;
  return "item_move_to_category";
}

/** #73 reverse merge: resolve the item_merge_into pseudo. The recipient is on a
 * CHILD Item (childTargetId) and picked an existing primary (raw.merge_primary_
 * item — a work_item slug). Mutates `raw` into the work_item_merge wire payload
 * (source = [this child]; the picker field dropped) and returns the POST target
 * id for the PRIMARY. The thread segment is cosmetic — the absorber's
 * _parse_work_item_target discards it and resolves the primary by work_item slug
 * project-wide — so the child's own thread is used and a cross-Category primary
 * still binds. Returns null if no primary was picked (the <select> is required,
 * so the empty case is already blocked client-side; the caller then keeps the
 * child target as a no-op fallback). Pure → unit-testable. */
export function resolveItemMergeInto(
  raw: Record<string, unknown>,
  childTargetId: string,
): string | null {
  const primary = String(raw.merge_primary_item ?? "").trim();
  delete raw.merge_primary_item;
  const childWi = (childTargetId.split("work_item:")[1] ?? "").trim();
  raw.source_work_item_slugs = childWi ? [childWi] : [];
  // #66 D4: `from_proposal` (set only when this form was opened by Accept on
  // an agent proposal) rides through UNTOUCHED to the wire payload. It is the
  // only positive label the dedup judge can get — an accepted proposal is
  // otherwise indistinguishable from a merge the recipient made unprompted.
  // Deliberately NOT deleted here, unlike merge_primary_item.
  if (!primary) return null;
  const childThread = childTargetId.match(/^thread:([^/]+)/)?.[1] ?? "";
  return childThread
    ? `thread:${childThread}/work_item:${primary}`
    : `unthreaded/work_item:${primary}`;
}

/** Cascade-close (2026-07-23): the children still to close — the full list
 * minus those already closed by THIS form instance (a retry after a partial
 * failure must never re-POST a child that succeeded). Pure → unit-testable. */
export function pendingCascadeChildren(
  children: Array<{ target_id: string; label: string }> | undefined,
  alreadyClosed: ReadonlySet<string>,
): Array<{ target_id: string; label: string }> {
  return (children ?? []).filter((c) => !alreadyClosed.has(c.target_id));
}

/** Cascade-close: the fixed marker stamped on each child's action_close so the
 * Closed Archive shows WHY it closed ("Closed with Item 3.26"). The form's
 * optional reason field rides the ITEM close only. Pure → unit-testable. */
export function cascadeChildReason(itemDisplayNumber?: string | null): string {
  return itemDisplayNumber
    ? `Closed with Item ${itemDisplayNumber}`
    : "Closed with parent Item";
}

/** B1 / #73 reverse merge (Action tier): resolve the action_merge_into pseudo.
 * The recipient is on a CHILD Action (childTargetId, a full action_item target
 * id) and picked an existing primary Action (raw.merge_primary_action — also a
 * full action_item target id, so the primary's Item is known for the POST
 * target). Mutates `raw` into the action_merge wire payload whose SOLE source
 * is the child's FULL target id (the picker field dropped) and returns the POST
 * target id = the picked primary. Passing the child's full id (not a bare
 * ai_slug) lets the existing submit-path cross-Item block re-parent the child
 * into the primary's Item before the merge, exactly as a forward action_merge
 * of a cross-Item source would. Returns null if no primary was picked (the
 * <select> is required, so the empty case is already blocked client-side; the
 * caller then keeps the child target as a no-op fallback). Pure → unit-testable. */
export function resolveActionMergeInto(
  raw: Record<string, unknown>,
  childTargetId: string,
): string | null {
  const primary = String(raw.merge_primary_action ?? "").trim();
  delete raw.merge_primary_action;
  // Source = the child's FULL target id (thread/work_item/action_item) so the
  // submit-path cross-Item re-parent sees the child's origin Item; the merge's
  // own ai_slug coercion (reshape) then narrows it. A child that already shares
  // the primary's Item simply skips the re-parent (wi === targetWi).
  raw.source_action_item_slugs = childTargetId ? [childTargetId] : [];
  if (!primary) return null;
  return primary;
}

/** Round-4 #2: decompose the action_edit_details pseudo-form into one wire
 * event per CHANGED field, judged against the Action's current values
 * (spec.current — the same source the form prefilled from):
 *  - reason / impact / off_hold_condition: submit only a NON-EMPTY changed
 *    value (clearing them isn't a wire operation — required metadata on
 *    Critical/Delay/On-Hold; "leave as-is to keep" is the documented contract);
 *  - due: "" with a prior value = an explicit CLEAR (action_set_due null,
 *    the existing wire semantics); a new date submits when changed.
 * Untouched prefilled fields produce NO event (no no-op corrections spamming
 * corrections.yaml). Pure → unit-testable. */
/**
 * N2/#117 — one-click "Accept proposed answer". Submits the recipient's
 * acceptance of the answer-proposer's suggestion by REUSING the existing
 * `action_open_question_resolve` wire kind (qid + answer) — no new kind, no
 * Worker change. Posts + dispatches `feedback-submitted` exactly like the tested
 * form submit, so the chip / undo / optimistic flow is identical. Direct-payload
 * shim (the plan's sanctioned path); throws on POST failure for the caller to
 * surface + re-enable its button.
 */
export async function acceptProposedAnswer(
  runId: string, actionTargetId: string, qid: string, answer: string,
): Promise<string> {
  const payload = { qid, answer };
  const res: { event_id: string } = inFixtureMode()
    ? { event_id: crypto.randomUUID() }
    : await postJson<{ event_id: string }>(
        `/feedback/${encodeURIComponent(runId)}`,
        {
          kind: "action_open_question_resolve",
          target: { type: "action_item", id: actionTargetId },
          payload,
        },
      );
  document.dispatchEvent(new CustomEvent("feedback-submitted", {
    detail: {
      event_id: res.event_id, kind: "action_open_question_resolve",
      payload, submitted_at: new Date().toISOString(),
      targetId: actionTargetId, targetType: "action_item",
    },
  }));
  return res.event_id;
}

export function collectActionDetailEdits(
  raw: Record<string, unknown>,
  current: Record<string, string | null | undefined> | undefined,
): Array<{ kind: FeedbackKind; payload: Record<string, unknown> }> {
  const edits: Array<{ kind: FeedbackKind; payload: Record<string, unknown> }> = [];
  const cur = (k: string): string => {
    const v = current?.[k];
    return typeof v === "string" ? v.trim() : "";
  };
  const TEXT_FIELDS: Array<[string, FeedbackKind]> = [
    ["reason", "action_set_reason"],
    ["impact", "action_set_impact"],
    ["off_hold_condition", "action_set_off_hold_condition"],
  ];
  for (const [field, kind] of TEXT_FIELDS) {
    if (!(field in raw)) continue;            // field not on this priority's form
    const v = typeof raw[field] === "string" ? (raw[field] as string).trim() : "";
    if (v && v !== cur(field)) edits.push({ kind, payload: { [field]: v } });
  }
  if ("due" in raw) {
    const v = typeof raw.due === "string" ? (raw.due as string).trim() : "";
    const c = cur("due");
    if (v !== c && (v || c)) {
      edits.push({ kind: "action_set_due", payload: { due: v || null } });
    }
  }
  return edits;
}

/** #60 PR4-v2: resolve the merge_into_existing pickers' friendly labels back to
 * stable slugs (mirror of resolvePickedTargetSlugs, against the OPEN+CLOSED
 * merge option sources). The action tier resolves its single pick into BOTH
 * slugs the agent's probe needs. Unresolved free text is left for reshape's
 * ensureSlug() fallback. Mutates `raw` in place; pure → unit-testable. */
export function resolveMergeTargetPicks(
  raw: Record<string, unknown>,
  mergeThreadOptions: ThreadOption[] = [],
  mergeItemOptions: ItemOption[] = [],
  mergeActionOptions: ClosedActionOption[] = [],
): void {
  const t = raw.target_thread_slug;
  if (typeof t === "string") {
    const bySlug = new Set(mergeThreadOptions.map((o) => o.slug));
    if (!bySlug.has(t)) {
      const byKey = new Map<string, string>();
      for (const o of mergeThreadOptions) {
        byKey.set(threadOptionLabel(o), o.slug);
        if (!byKey.has(o.name)) byKey.set(o.name, o.slug);
      }
      if (byKey.has(t)) raw.target_thread_slug = byKey.get(t);
    }
  }
  const i = raw.target_work_item_slug;
  if (typeof i === "string") {
    const bySlug = new Set(mergeItemOptions.map((o) => o.slug));
    if (!bySlug.has(i)) {
      const byLabel = new Map(
        mergeItemOptions.map((o) => [itemOptionLabel(o), o.slug]),
      );
      if (byLabel.has(i)) raw.target_work_item_slug = byLabel.get(i);
    }
  }
  const a = raw.target_action_pick;
  if (typeof a === "string" && a.trim()) {
    const byLabel = new Map(
      mergeActionOptions.map((o) => [o.label, o]),
    );
    const hit = byLabel.get(a);
    if (hit) {
      raw.target_work_item_slug = hit.work_item_slug;
      raw.target_action_item_slug = hit.action_item_slug;
    } else {
      // Free-typed action text: best-effort slug; the parent Item is
      // unknown, so the absorber dead-letters with suggestions.
      raw.target_action_item_slug = ensureSlug(a);
    }
  }
}

export type FeedbackKind =
  | "thread_approve"
  | "thread_confirm"
  | "thread_promote"
  | "thread_reject"
  | "thread_rename"
  | "thread_merge"
  | "thread_retag"
  | "thread_close"
  | "thread_reopen"
  | "work_item_close"
  | "work_item_reopen"
  | "responsible_party_assign"
  | "review_clarification"
  | "date_correction"
  | "not_relevant"
  | "missing_item"
  | "comment"
  | "flag_next_week"
  // v4 (Task #9) — Category → Item → Action correction kinds.
  // Each binds against an agent-side Pydantic literal in
  // agent/corrections.py (Task #8) and a worker schema literal in
  // worker/src/schemas.ts. Strict ordering: agent shipped first.
  | "action_priority_change"
  | "action_set_reason"
  | "action_set_impact"
  | "action_set_off_hold_condition"
  | "action_set_due"
  | "action_open_question_add"
  | "action_open_question_resolve"
  // Client 2026-08-12: dispose of an open question without answering it.
  | "action_open_question_dismiss"
  | "action_kil_mark"
  | "action_kil_unmark"
  // Decision 12.8 (2026-05-28): Action lifecycle + closure-candidate.
  | "action_close"
  | "action_reopen"
  | "action_dismiss_closure_candidate"
  // Option Y (2026-06-03): dismiss an Item-level closure proposal.
  | "work_item_dismiss_closure_candidate"
  // #66 D4 (2026-08-14): reject a proposed Item MERGE. Pair-keyed — the payload
  // carries target_work_item_slug, because the hint is about two Items and is
  // re-derived every run, so a dismissal recorded against one Item would
  // suppress every future proposal on it.
  | "work_item_dismiss_merge_candidate"
  // B2 (2026-06-26): acknowledge a PLACED Item/Action ("seen, in-project, NOT
  // tracked") → status='acknowledged' (3rd terminal state). Distinct from close
  // (work done) + the Needs-Input informational_acknowledge. Mirror of
  // agent/portal.py FeedbackKind + worker/src/schemas.ts.
  | "work_item_acknowledge"
  | "action_acknowledge"
  // B2 Track-this: reverse an acknowledge (acknowledged -> open). Mirror of
  // agent/portal.py FeedbackKind + worker/src/schemas.ts.
  | "work_item_track"
  | "action_track"
  // B2 PR-1: reject a PLACED Item/Action as "not relevant" (not this project)
  // → status='not_relevant' (4th terminal state). The placed analogue of the
  // bucket `not_relevant`. Mirror of agent/portal.py + worker/src/schemas.ts.
  | "work_item_not_relevant"
  | "action_not_relevant"
  // R2-3: a recipient DIRECTLY edits an Item/Action narrative (verbatim).
  // Mirror of agent/portal.py + worker/src/schemas.ts.
  | "work_item_set_narrative"
  | "action_set_narrative"
  // R2-3 fast-follow: a recipient directly edits the executive summary (a
  // project-level singleton; targets `project:<slug>`). Mirror of
  // agent/portal.py + worker/src/schemas.ts.
  | "set_executive_summary"
  | "item_kil_mark"
  | "item_kil_unmark"
  | "item_add"
  | "category_add"
  | "action_add"
  | "place_from_unfiled"
  // A³/A² (2026-06-03): tier-explicit Place on Needs-Your-Input entries +
  // the informational "Dismiss (acknowledged)" disposition.
  | "place_as_category"
  | "place_as_action"
  | "informational_acknowledge"
  // #60 PR4-v2 (2026-06-11 review): the row's placement surface is TWO verbs.
  // `place_new` is an SPA-only PSEUDO-kind — its tier select resolves to the
  // real wire kind (place_as_category | place_from_unfiled | place_as_action)
  // at submit, with a `complete` flag for born-closed. `merge_into_existing`
  // (renamed from file_under_closed) folds a row into an existing entity.
  | "place_new"
  | "merge_into_existing"
  | "item_move_to_category"
  | "invoice_add"
  | "invoice_status_change"
  | "invoice_blocking_note_set"
  // Task 3: work-item structural affordances. Bind to agent-side
  // Pydantic literals (corrections.py) + worker schema (schemas.ts).
  | "candidate_to_item"
  | "work_item_merge"
  | "work_item_rename"
  // Open improvement #3: action-level merge.
  | "action_merge"
  // B6: action-level rename.
  | "action_rename"
  // B8: move an Action to a different Item.
  | "action_move_to_item"
  // #76: promote an Action to a new Item (+ relocate to a chosen Category).
  // Targets the action_item; the absorber creates a work_item + closes the
  // source. Mirror of agent/portal.py + worker/src/schemas.ts.
  | "action_to_item"
  // #38: sub-category grouping inside a Category (overlay tier). Mirror of
  // agent/portal.py FeedbackKind + worker/src/schemas.ts. add targets the
  // parent Category (thread); rename/close/reopen target the subcategory;
  // item_move_to_subcategory targets the work_item.
  | "subcategory_add"
  | "subcategory_rename"
  | "subcategory_close"
  | "subcategory_reopen"
  // #74b: delete (evaporate) a sub-category — ungroup members + drop the row
  // (no tombstone, no close-gate; distinct from subcategory_close).
  | "subcategory_delete"
  | "item_move_to_subcategory"
  // #37: re-band an open Category into one of the 4 fixed proposal phases.
  // Targets the parent thread (Category); payload {to_phase}. Pure attribute
  // move — no relocation, latest-wins on replay. Mirror of agent/portal.py +
  // agent/corrections.py CategoryMoveToPhase + worker/src/schemas.ts.
  | "category_move_to_phase"
  // #38 round-1 review (2026-06-12, feedback 2.1): `item_move` is an SPA-only
  // PSEUDO-kind (the place_new pattern) — ONE "Move to…" button whose tier
  // select resolves to item_move_to_category | item_move_to_subcategory at
  // submit via resolveItemMoveKind. Never reaches the wire; offered only when
  // BOTH destinations exist (open Item + ≥1 open group in the parent).
  | "item_move"
  // #73 (2026-06-16 call): `item_merge_into` is an SPA-only PSEUDO-kind on a
  // CHILD Item's menu — the reverse of work_item_merge (which folds others INTO
  // the open item). The recipient picks an existing primary to fold THIS Item
  // up into, without backtracking to the primary first; at submit it resolves
  // to a standard work_item_merge whose TARGET is the picked primary and whose
  // single source is this child. No new wire kind (feedback-spa-pseudo-kind-
  // pattern). Offered only when ≥1 other open Item exists (a valid primary).
  | "item_merge_into"
  // B1 / #73 (2026-06-23 Ray, his #1 merge pain): `action_merge_into` is the
  // ACTION-tier twin of item_merge_into — an SPA-only PSEUDO-kind on a CHILD
  // Action's menu, the reverse of action_merge (which folds others INTO the
  // open Action). The recipient picks an existing primary Action to fold THIS
  // Action up into, without backtracking to the primary and hunting the child
  // in a list. At submit it resolves to a standard action_merge whose TARGET is
  // the picked primary and whose single source is this child; a cross-Item pick
  // re-parents this child into the primary's Item first (the existing action_
  // merge cross-Item path). No new wire kind — inherits action_merge's absorb
  // (alias capture = the #66 ledger signal) + optimistic gray-out. Offered only
  // when ≥1 other open Action exists project-wide (a valid primary).
  | "action_merge_into"
  // Round-4 review (2026-06-12 #2): `action_edit_details` is an SPA-only
  // PSEUDO-kind flattening the per-field metadata edit buttons (reason /
  // impact / due / off-hold) into ONE form. Unlike the tier-select pseudos it
  // decomposes at submit into one EXISTING wire event per CHANGED field
  // (action_set_reason / _impact / _off_hold_condition / _due) via
  // collectActionDetailEdits — never reaches the wire itself. Offered only
  // when the priority surfaces ≥2 edit fields (Active keeps its lone
  // "Edit due date" button — the no-one-option-nesting rule).
  | "action_edit_details"
  // Cascade-close (Ray 2026-07-21, built 2026-07-23): `work_item_close_cascade`
  // is an SPA-only PSEUDO-kind replacing the greyed dead-end "Close Item" when
  // the Item still has OPEN child Actions — Ray's biggest confusion cluster
  // (the B9 gate greyed close and nothing cascaded). The form lists the open
  // children for confirmation; at submit it sequentially POSTs one EXISTING
  // `action_close` per child (each stamped "Closed with Item <N>") and then
  // rides the generic path as a plain `work_item_close` whose #74a
  // client_events carry the just-POSTed child closes past the Worker's
  // open-children gate (the proven B10 contingent-close pattern). No new wire
  // kind, no Worker change. Offered only when EVERY open child has an ai_slug
  // (else the old greyed close + notice remain — an untargetable child would
  // strand the composite).
  | "work_item_close_cascade";

export type TargetType =
  | "thread"
  | "item"
  | "section"
  | "extraction"
  | "work_item"
  // #38: sub-category targets — `thread:<t>/subcategory:<sc>`
  // (subcategory_rename / _close / _reopen). add targets the parent thread;
  // item_move_to_subcategory targets the work_item.
  | "subcategory"
  // v4: action-item-scoped events (priority change, KIL mark,
  // question add/resolve, invoice ops on a specific action_item)
  // and unfiled-row events (place_from_unfiled).
  | "action_item"
  | "unfiled"
  // A³/A² (2026-06-03): informational-bucket-row events — `informational:<id>`.
  | "informational"
  // v4 (Decision 4.3): project-shell-scoped events. Currently only
  // used by `category_add` from the "+ Add Category" affordance at
  // the top of the briefing view.
  | "project";

export interface SubmittedEvent {
  event_id: string;
  kind: string;
  payload: Record<string, unknown>;
  submitted_at: string;
  // B7: false once poll-portal has absorbed the event — then Retract is
  // hidden (the inverse correction is the path). Undefined/true → retractable.
  pending?: boolean;
}

export interface FormControlSpec {
  runId: string;
  targetType: TargetType;
  targetId: string;
  kinds: string[];
  submitted?: SubmittedEvent[];
  // Optional advisory shown inside the popover when the user opens it
  // (above the action picker). Used by the thread header to explain
  // "Close N open work items before closing this thread" without
  // polluting the always-on header chrome. Free-form short string.
  notice?: string;
  // Slice 8 Item #5: project participants — used by the
  // responsible_party_assign form to populate a <datalist> for the
  // Owner name field. Sourced from the briefing (see
  // collectParticipantOptions in briefings.ts). When picking a name
  // that has an associated email, the form auto-fills the email field.
  participantOptions?: ParticipantOption[];
  // Contract-drift batch 2 — Finding 5: known threads on this briefing,
  // used to populate a <datalist> for the thread_merge "Merge into" and
  // thread_retag "New thread" forms. The operator picks by visible name;
  // the form submits the slug. Sourced from collectThreadOptions in
  // briefings.ts. Free-text fallback: reshapePayloadForKind slugifies
  // any value that doesn't already look like a slug.
  threadOptions?: ThreadOption[];
  // Decision 7A (close-out 2026-05-25): Items in scope when an Unfiled
  // row's "place under existing Item" path is exercised. Renders as a
  // <datalist> on `place_from_unfiled.target_work_item_slug`.
  itemOptions?: ItemOption[];
  // #38: the parent Category's sub-categories. Drives the
  // item_move_to_subcategory picker (open groups + "(ungrouped)") and the
  // subcategory_add slug-collision set (open + closed). Fed from the
  // optimistically-mutated payload so session-pending adds are visible
  // (the pending-aware picker mechanism — the cited "#60 pattern" never
  // existed; this is the real one, per the binding amendment).
  subcategoryOptions?: SubcategoryOption[];
  // Open improvement #3: sibling Actions under the parent Item, used by
  // the action_merge multiselect (the source Actions to fold into this
  // one). The target Action is excluded by the caller / form.
  actionOptions?: ActionOption[];
  // Decision 7A: invoices on the parent Item, used for invoice_status_change
  // and invoice_blocking_note_set instead of free-text invoice number.
  invoiceOptions?: InvoiceOption[];
  // Dogfood feedback 3: the Action's UNANSWERED open questions, so the
  // action_open_question_resolve form lets the recipient pick the question by
  // its TEXT (a <select>) instead of typing a cryptic 0-based index. value =
  // the question's index in open_questions_v4 (what the absorber expects).
  openQuestionOptions?: { value: string; label: string }[];
  // Client 2026-08-12 — the per-QUESTION ✎ menu. When set, the controls belong
  // to one specific question rather than to the Action as a whole, so the
  // "Which question" picker is dropped entirely and `qid` is injected at submit
  // (mirroring how `bucketContext` injects `source_ref` / `unfiled_id`).
  //
  // This is what let the Action-level `action_open_question_resolve` picker go.
  // 14 of 45 live Actions carry more than one question (one carries five), so
  // answering meant opening the Action's menu and re-identifying the question in
  // a dropdown — while the question itself was on screen the whole time.
  questionContext?: QuestionContext;
  // Decision 3A: current priority of the Action target — drives
  // kindsForActionV4 filtering at the briefings.ts caller. Echoed
  // through to allow form-level "Add/Edit X" label adjustments.
  // Decision 12.5: Informational is a current-state value (synth-only,
  // not manually settable). The form's priority dropdown still lists
  // only the 4 settable priorities — Informational is rendered as
  // current state but not as a pick-from option.
  priorityStatus?: "Critical" | "Delay Potential" | "Active" | "On Hold" | "Informational";
  // Decision 3B: populated-field state on the Action target. The
  // single-field forms (action_set_reason / impact / off_hold_condition /
  // due) flip their button label to "Add" vs "Edit" based on this.
  populatedFields?: ReadonlySet<string>;
  // Current values on the target, keyed by field name (reason / impact /
  // off_hold_condition / due / text). Edit forms pre-fill from this so the
  // recipient edits the existing value rather than starting blank. Only the
  // edit kinds in PREFILL_KINDS consume it (never the "add new" forms).
  current?: Record<string, string | null | undefined>;
  // N1 (2026-07-14): the agent's matter merge suggestion(s) for an Item — drives
  // the orange Accept/Dismiss proposal banner at the top of the picker (showPicker),
  // mirroring the closure_candidate affordance. `targets` holds every resolved
  // candidate (default the first); ≥2 → the banner shows a dropdown to pick which.
  // Accept opens the pre-filled item_merge_into form (merge_primary_item set to the
  // chosen target); Dismiss hides the banner (session-local). Absent → no banner.
  // #66 D1-D3 (2026-07-23): `reason` = the dedup-consult overlap tell, shown
  // as a muted line under the banner lead; `review` swaps the one-click
  // auto-submit for a VISIBLE prefilled item_merge_into form (whole-Item fold
  // scope guard — set when folding a multi-action Item on an overlap signal).
  mergeProposal?: {
    targets: Array<{ slug: string; label: string }>;
    reason?: string;
    review?: boolean;
    // #66 D4: the subset of `targets` that came from a real merge_candidate
    // (the dedup tell or the judge) rather than from the matter LEDGER. The
    // banner shows the union, but only these may be recorded as a DURABLE
    // dismissal — `merge_candidate_dismissals` suppresses the dedup judge, so
    // a matter-only slug written there would permanently mute a pair the judge
    // never proposed, with no UI to inspect or undo it. Matter targets keep the
    // localStorage-only dismiss they had before D4.
    durableDismissSlugs?: string[];
    // #66 D4: stable id of the proposal this banner represents ("<slug>|<slug>",
    // sorted). Rides onto an accepted merge as `from_proposal` so accepts can
    // be counted against proposals — the positive half of the label set.
    proposalId?: string;
  };
  // #66 D1-D3: overlap-aware localStorage dismiss key for the banner + the
  // inline ◇ notes (briefings.ts mergeBannerDismissKey). Defaults to targetId.
  mergeProposalDismissKey?: string;
  // A①: the merge target's display label ("1.1 Hearing preparation"),
  // threaded into the work_item_merge / action_merge event payload so the
  // optimistic "Recently Merged → into X" treatment can name the target and
  // survive reload (the label rides the POSTed event, not the payload).
  mergeTargetLabel?: string;
  // B10 (2026-06-03): whether the Item is closeable (0 open Actions). Drives
  // the "Also close this Item" checkbox in the Move-to-Category form — enabled
  // when closeable, disabled + B9 note otherwise. Set by the Item-header caller.
  itemCloseable?: boolean;
  // Cascade-close (2026-07-23): the Item's OPEN child Actions, threaded by the
  // Item-header caller when it offers work_item_close_cascade. Drives the
  // form's read-only confirmation list + the submit pre-loop's per-child
  // action_close POSTs. Every entry has a resolvable action_item target id
  // (the caller only offers the cascade when ALL open children have ai_slugs).
  cascadeOpenActions?: Array<{ target_id: string; label: string }>;
  // A³/A² (2026-06-03): the Needs-Your-Input bucket entry's stable identity,
  // threaded from renderUnfiledV4 / renderInformationalV4. The Place tiers
  // (place_from_unfiled / place_as_category / place_as_action) need the
  // `source_ref` (for next-run bucket suppression) + the entry `id`; neither
  // is a visible form field, so the submit handler injects them from here.
  bucketContext?: { sourceRef: string; entryId: string;
    // #60: the entry's display text + narrative — prefill the Place-as-New
    // name/text and narrative fields (editable); text snapshots the Merge note.
    entryText?: string;
    entryNarrative?: string;
    // #60 PR4-v2 (#1.4): set on rows folded in from a candidate thread, so a
    // Place/Merge submit retires the inferred thread (the #57 exit).
    candidateSlug?: string };
  // #60 PR4-v2 Merge-Into-Existing pickers — existing entities at each tier,
  // OPEN and CLOSED (the row may dedup against either). Separate option
  // sources from threadOptions/itemOptions (those stay open-only for the
  // Place pickers, per the 2026-06-09 closed/candidate exclusion). Items
  // union the briefing blob with the all-ages Closed-Items snapshot.
  mergeThreadOptions?: ThreadOption[];
  mergeItemOptions?: ItemOption[];
  mergeActionOptions?: ClosedActionOption[];
  // Decision 3.6 breadcrumb: the moving entity's current path notation
  // ("3.7" for an Item, "3.7.2" for an Action), threaded into the
  // item_move_to_category / action_move_to_item event as
  // `former_display_number` so next week's render can show
  // "(formerly 3.7, recategorized DATE)". Not a visible form field — the
  // submit handler injects it from here (like mergeTargetLabel).
  movingFromDisplayNumber?: string | null;
}

export interface ParticipantOption {
  name: string;
  email?: string | null;
}

export interface ThreadOption {
  slug: string;
  name: string;
  display_number?: string | null;
  // #60 PR4-v2: set on Merge-Into-Existing option sources so the Open/Closed
  // toggle can filter. Unused by the open-only Place pickers.
  closed?: boolean;
  // #75: this Category's OPEN sub-categories, for the deep-move destination
  // picker ("Category › Sub-category" in one step). Mirrors ThreadOptionView.
  subcategories?: { slug: string; name: string }[];
}

/** #1a UX (2026-06-19): the deep-move picker is a CASCADE — first pick a
 * Category, then a second <select> surfaces THAT Category's sub-categories.
 * `moveCascadeCategoryOptions` builds the first <select> (open Categories,
 * bare `<thread_slug>` values). Replaces the single flattened "Category ›
 * Sub-category" list (functional but undiscoverable when moving fast —
 * operator feedback 2026-06-19). */
export function moveCascadeCategoryOptions(
  threadOptions: ThreadOption[],
): { value: string; label: string }[] {
  return threadOptions
    .filter((t) => !t.closed)
    .map((t) => ({ value: t.slug, label: threadOptionLabel(t) }));
}

/** #1a UX: the dependent second <select> — the picked Category's destinations.
 * Always leads with a "(directly under the Category)" option (the bare
 * `<thread_slug>`), then one entry per open sub-category
 * (`<thread_slug>/<subcategory_slug>`, slugs never contain "/"). The encoded
 * value is the SAME composite the flattened list produced, so the submit path
 * (planDeepMoveDestination) and the wire are unchanged. */
export function moveCascadeSubcategoryOptions(
  threadOptions: ThreadOption[],
  categorySlug: string,
): { value: string; label: string }[] {
  const cat = threadOptions.find((t) => t.slug === categorySlug && !t.closed);
  if (!cat) return [];
  const out = [{ value: cat.slug, label: "(Ungrouped)" }];
  for (const sc of cat.subcategories ?? []) {
    out.push({ value: `${cat.slug}/${sc.slug}`, label: sc.name });
  }
  return out;
}

/** #1b deep-placement: the place-form's dependent Sub-category <select> for the
 * picked Category. Unlike moveCascadeSubcategoryOptions (which encodes the
 * Category into the value for the move composite), the place payload carries
 * new_item_thread_slug + new_item_subcategory_slug as SEPARATE fields, so each
 * option value is the BARE sub-category slug — or "" for "(Ungrouped)" (place
 * directly under the Category; reshape strips the empty value). */
export function placeSubcategoryOptions(
  threadOptions: ThreadOption[],
  categorySlug: string,
): { value: string; label: string }[] {
  const cat = threadOptions.find((t) => t.slug === categorySlug && !t.closed);
  const out = [{ value: "", label: "(Ungrouped)" }];
  for (const sc of cat?.subcategories ?? []) {
    out.push({ value: sc.slug, label: sc.name });
  }
  return out;
}

/** #1a/#1b: wire a Category → Sub-category cascade — on the driver <select>'s
 * change, repopulate the dependent <select> via `optionsFor(driverValue)`,
 * defaulting to the first option. A no-op when either <select> isn't in this
 * form (so the same call is safe across every form). Mirrors the merge-scope
 * repopulate pattern. */
function wireSubcategoryCascade(
  form: HTMLFormElement,
  driverName: string,
  dependentName: string,
  optionsFor: (driverValue: string) => { value: string; label: string }[],
): void {
  const driver = form.querySelector<HTMLSelectElement>(
    `select[name="${driverName}"]`,
  );
  const dependent = form.querySelector<HTMLSelectElement>(
    `select[name="${dependentName}"]`,
  );
  if (!driver || !dependent) return;
  const repopulate = (): void => {
    dependent.replaceChildren();
    for (const o of optionsFor(driver.value)) {
      const opt = document.createElement("option");
      opt.value = o.value;
      opt.label = o.label;
      opt.textContent = o.label;
      dependent.appendChild(opt);
    }
    dependent.selectedIndex = 0;
  };
  driver.addEventListener("change", repopulate);
  repopulate();
}

/** #75: decode the deep-move destination picked in `to_thread_slug` (a bare
 * thread slug, or `thread/subcat`) and rewrite `raw` into the right wire move.
 * Returns the resolved wireKind plus an optional grouping step to chain after a
 * cross-Category move. Mutates `raw`; pure → unit-testable.
 *  - bare destination → item_move_to_category (current behavior);
 *  - dest is a sub-category of ANOTHER Category → item_move_to_category now +
 *    chain item_move_to_subcategory (the destination Category is where the
 *    group lives);
 *  - dest is a sub-category of the CURRENT Category → just item_move_to_subcategory
 *    (no Category move, so no bogus "(formerly …)" breadcrumb). */
export function planDeepMoveDestination(
  raw: Record<string, unknown>,
  currentThreadSlug: string,
  wireKind: FeedbackKind,
): { wireKind: FeedbackKind; chainSubcategory: string | null; chainThread: string } {
  // #1a UX: the cascade's Category <select> is UI-only — the picked Category is
  // already encoded in to_thread_slug's composite. Prune it so it never rides
  // the wire payload (reshape copies every key).
  delete raw.to_category_pick;
  if (wireKind !== "item_move_to_category") {
    return { wireKind, chainSubcategory: null, chainThread: "" };
  }
  const dest = String(raw.to_thread_slug ?? "");
  const slash = dest.indexOf("/");
  if (slash === -1) {
    return { wireKind, chainSubcategory: null, chainThread: "" };
  }
  const destThread = dest.slice(0, slash);
  const subcat = dest.slice(slash + 1);
  if (destThread === currentThreadSlug) {
    // Same Category — group in place; drop the Category-move-only fields so no
    // spurious move/breadcrumb/close rides along.
    delete raw.to_thread_slug;
    delete raw.also_close;
    delete raw.former_display_number;
    raw.to_subcategory_slug = subcat;
    return { wireKind: "item_move_to_subcategory", chainSubcategory: null, chainThread: "" };
  }
  raw.to_thread_slug = destThread;
  return { wireKind: "item_move_to_category", chainSubcategory: subcat, chainThread: destThread };
}

// #60: one existing Action offered as a Merge-Into-Existing target. The picker
// needs BOTH slugs (the agent's action-tier probe keys on work_item +
// action_item), so the label resolves back to the pair at submit.
export interface ClosedActionOption {
  work_item_slug: string;
  action_item_slug: string;
  // Pre-built display label: "N.N.N <action text> (<Item name>)".
  label: string;
  closed?: boolean;
}

export interface ItemOption {
  slug: string;
  name: string;
  // Optional display number ("3.1") shown alongside the name in the
  // picker. Decision 6 numeric ordering applies; SPA passes this for
  // operator convenience.
  display_number?: string | null;
  thread_name?: string | null;
  closed?: boolean;
}

// #38: one sub-category of the parent Category, offered to the
// item_move_to_subcategory picker (open groups + "(ungrouped)") and used as
// the subcategory_add slug-collision set (open AND closed). Fed from the
// (optimistically mutated) blob in briefings.ts, so session-pending adds are
// already present — the binding amendment's pending-aware picker mechanism.
export interface SubcategoryOption {
  slug: string;
  name: string;
  // true when status === "closed": excluded from the move picker, still
  // counted for slug collision (the UNIQUE space spans open + closed).
  closed?: boolean;
}

export interface ActionOption {
  ai_slug: string;
  text: string;
  display_number?: string | null;
  // B8 cross-Item action_merge: when the merge picker offers Actions from
  // OTHER Items, each option carries its full target id (thread/work_item/
  // action_item) + its parent Item slug + a human label. The submit handler
  // re-parents a cross-Item source into the target's Item (action_move_to_item)
  // before merging. Absent for the sibling-only case (same-Item merge).
  target_id?: string;
  work_item_slug?: string;
  item_label?: string;
}

export interface InvoiceOption {
  number: string;
  // Short label combining number + amount + status to disambiguate
  // entries in the dropdown.
  label: string;
}

// Decision 12.17 (2026-05-28, operator follow-up): CTAs carry the
// entity noun ("Close Category", "Close Item", "Close Action") so
// the recipient knows what they're closing. Retires the internal-
// vocab "thread" leak — surfaces as "Category" everywhere per
// [[role-terminology]]. Invoice labels capitalize the noun for
// parity with Category/Item/Action.
const KIND_LABEL: Record<string, string> = {
  thread_approve: "Approve",
  thread_confirm: "Confirm Category",
  thread_promote: "Promote to Category",
  thread_reject: "Reject as Not Relevant",
  thread_rename: "Rename Category",
  thread_merge: "Merge this Category into another",
  thread_retag: "Move to another Category",
  thread_close: "Close Category",
  thread_reopen: "Reopen Category",
  work_item_close: "Close Item",
  // Cascade-close: the one-click composite offered when open children exist.
  work_item_close_cascade: "Close Item & its open Actions",
  work_item_reopen: "Reopen Item",
  responsible_party_assign: "Assign owner",
  review_clarification: "Clarify",
  date_correction: "Edit date",
  not_relevant: "Not relevant",
  // Round-4 (2026-06-12 #1): was "Add missing Item", a near-synonym of
  // item_add's "Add Item" — the recipient couldn't tell self-serve creation
  // from describe-it-for-the-operator. The verb now carries the distinction.
  missing_item: "Report something missing",
  comment: "Comment",
  flag_next_week: "Flag next week",
  // v4 (Task #9). Action affordances render as a flat picker per
  // Action row; the labels lean active-voice and short so the
  // popover stays tight.
  action_priority_change: "Change priority",
  action_set_reason: "Edit reason",
  action_set_impact: "Edit impact",
  action_set_off_hold_condition: "Edit off-hold condition",
  action_set_due: "Edit due date",
  action_open_question_add: "Add open question",
  action_open_question_resolve: "Answer question",
  action_open_question_dismiss: "Dismiss question",
  action_kil_mark: "Mark for Project Status Update",
  action_kil_unmark: "Unmark Project Status Update",
  // Decision 12.8 (2026-05-28). action_close handles both manual
  // close (no candidate present) and "approve closing" (candidate
  // present). Same correction kind, same downstream effect.
  action_close: "Close Action",
  action_reopen: "Reopen Action",
  action_dismiss_closure_candidate: "Keep open (dismiss proposal)",
  // Option Y (2026-06-03): parks an action-less Item open instead of closing.
  work_item_dismiss_closure_candidate: "Keep Item open (dismiss proposal)",
  work_item_dismiss_merge_candidate: "Not a duplicate (dismiss merge proposal)",
  // B2 (2026-06-26): the disposition vocab's "seen, not tracked" verb — drops
  // the row from the active briefing into the Closed Archive's "Acknowledged /
  // Not Tracked" section, NEVER recording it as completed work.
  work_item_acknowledge: "Acknowledge (not tracked)",
  action_acknowledge: "Acknowledge (not tracked)",
  // B2 Track-this: bring an acknowledged entry back into active tracking.
  work_item_track: "Track this",
  action_track: "Track this",
  // B2 PR-1: the disposition vocab's "not this project" verb — rejects a placed
  // entity (a misclassification), dropping it from the briefing for good (not
  // archived, not resurfaceable). Records a `rejected` training signal.
  work_item_not_relevant: "Not relevant",
  action_not_relevant: "Not relevant",
  // R2-3: direct narrative edit (the structured alternative to a comment).
  work_item_set_narrative: "Edit narrative",
  action_set_narrative: "Edit narrative",
  // R2-3 fast-follow: direct edit of the executive summary.
  set_executive_summary: "Edit summary",
  item_kil_mark: "Mark Item for Project Status Update",
  item_kil_unmark: "Unmark Item Project Status Update",
  item_add: "Add Item",
  category_add: "Add Category",
  action_add: "Add Action",
  place_from_unfiled: "Place as Item",
  // A³/A² (2026-06-03): tier-explicit Place on Needs-Your-Input entries.
  place_as_category: "Place as Category",
  place_as_action: "Place as Action",
  informational_acknowledge: "Dismiss (acknowledged)",
  place_new: "Place as New (Category · Item · Action)",
  merge_into_existing: "Merge Into Existing (Category · Item · Action)",
  item_move_to_category: "Move to",
  invoice_add: "Add Invoice",
  invoice_status_change: "Change Invoice status",
  invoice_blocking_note_set: "Set Invoice blocking note",
  // Task 3: work-item structural affordances.
  candidate_to_item: "Place as Item under Category",
  // Merge direction disambiguated by SUBJECT (Ray 6/30 dogfood: "Merge
  // Items" vs "Merge this Item into…" read the same). "other … into this
  // one" = THIS survives, others fold in; "this … into another" = THIS is
  // folded away into a picked primary.
  work_item_merge: "Merge other Items into this one",
  // #73: reverse merge — fold THIS Item up into a picked existing primary.
  item_merge_into: "Merge this Item into another",
  work_item_rename: "Rename Item",
  // Open improvement #3: action-level merge.
  action_merge: "Merge other Actions into this one",
  // B1 / #73: reverse merge — fold THIS Action up into a picked existing primary.
  action_merge_into: "Merge this Action into another",
  // B6: action-level rename.
  action_rename: "Rename Action",
  action_move_to_item: "Move to another Item",
  // #76: promote this Action up to its own Item.
  action_to_item: "Promote to Item",
  // #38: sub-category grouping. "Sub-category" is the client-facing label
  // (GLOSSARY.md); internal slug stays `subcategory`.
  subcategory_add: "Add Sub-category",
  subcategory_rename: "Rename Sub-category",
  subcategory_close: "Close Sub-category",
  // #74b: delete (evaporate) — distinct from Close (which tombstones).
  subcategory_delete: "Delete Sub-category",
  subcategory_reopen: "Reopen Sub-category",
  item_move_to_subcategory: "Move to Sub-category",
  // #37: re-band the Category under a different proposal phase.
  category_move_to_phase: "Move to Phase",
  // Feedback 2.1: the combined Move button (pseudo-kind; shown only when both
  // destinations exist — otherwise the plain "Move to Category" renders).
  item_move: "Move to…",
  // Round-4 #2: the flattened metadata-edit button (pseudo-kind; shown when
  // the priority surfaces ≥2 edit fields — Active keeps "Edit due date").
  action_edit_details: "Edit details",
};

// Menu grouping (2026-07, R2 + #93). Ray 6/30: the feedback menus are "a
// little unwieldy" — a loaded Action pencil is a flat wall of up to ~19
// buttons, and he couldn't spot "Keep open (dismiss proposal)" in it. The
// picker below sections the flat kind list into labeled groups (static
// headings — no collapse, so zero added clicks). This is IN-PLACE
// sectioning of the SAME popover, not a relocated panel (the close-panel
// Ray rejected 6/19 — feedback-inline-over-separate-panel). The kind
// arrays (kindsFor*V4) are unchanged; grouping is a render-only concern,
// and within-section order preserves the operator-tuned sequence.
//
// One `id` per semantic cluster; SECTION_ORDER fixes their top-to-bottom
// order (only non-empty sections render). "" label = a heading-less
// trailing bucket so an unmapped/new kind still shows (never disappears).
export const MENU_SECTIONS: ReadonlyArray<{ id: string; label: string }> = [
  { id: "place", label: "Place" },
  { id: "status", label: "Status" },
  { id: "edit", label: "Edit" },
  { id: "reorganize", label: "Reorganize" },
  { id: "add", label: "Add" },
  { id: "psu", label: "Project status update" },
  { id: "invoices", label: "Invoices" },
  { id: "notes", label: "Notes" },
  { id: "other", label: "" },
];

const SECTION_BY_KIND: Record<string, string> = {
  // Place — Needs-Your-Input triage verbs (surface an unplaced row into
  // the tree, or reject it).
  place_new: "place", merge_into_existing: "place", place_from_unfiled: "place",
  place_as_category: "place", place_as_action: "place", candidate_to_item: "place",
  thread_promote: "place", thread_approve: "place", thread_confirm: "place",
  thread_reject: "place",
  // Status — lifecycle (close / reopen / keep-open) + the B2 disposition
  // verbs (acknowledge / not relevant / track) + sub-category delete.
  thread_close: "status", thread_reopen: "status",
  work_item_close: "status", work_item_close_cascade: "status",
  work_item_reopen: "status",
  work_item_dismiss_closure_candidate: "status",
  work_item_dismiss_merge_candidate: "status",
  work_item_acknowledge: "status", work_item_not_relevant: "status",
  work_item_track: "status",
  action_close: "status", action_reopen: "status",
  action_dismiss_closure_candidate: "status",
  action_acknowledge: "status", action_not_relevant: "status",
  action_track: "status",
  subcategory_close: "status", subcategory_reopen: "status",
  subcategory_delete: "status",
  informational_acknowledge: "status", not_relevant: "status",
  // Edit — narrative / rename / priority / per-field metadata / assign
  // owner / resolve open question / exec-summary.
  thread_rename: "edit",
  work_item_set_narrative: "edit", work_item_rename: "edit",
  responsible_party_assign: "edit",
  action_set_narrative: "edit", action_rename: "edit",
  action_priority_change: "edit", action_set_reason: "edit",
  action_set_impact: "edit", action_set_off_hold_condition: "edit",
  action_set_due: "edit", action_edit_details: "edit",
  action_open_question_resolve: "edit", action_open_question_add: "edit",
  action_open_question_dismiss: "edit",
  subcategory_rename: "edit", set_executive_summary: "edit",
  review_clarification: "edit", date_correction: "edit",
  // Reorganize — merge / move / promote / re-band.
  thread_merge: "reorganize", thread_retag: "reorganize",
  category_move_to_phase: "reorganize",
  work_item_merge: "reorganize", item_merge_into: "reorganize",
  item_move: "reorganize", item_move_to_category: "reorganize",
  item_move_to_subcategory: "reorganize",
  action_merge: "reorganize", action_merge_into: "reorganize",
  action_move_to_item: "reorganize", action_to_item: "reorganize",
  // Add — create a child / report a gap.
  item_add: "add", missing_item: "add", action_add: "add",
  subcategory_add: "add", category_add: "add",
  // Project status update — the PSU (KIL) mark toggle.
  action_kil_mark: "psu", action_kil_unmark: "psu",
  item_kil_mark: "psu", item_kil_unmark: "psu",
  // Invoices — the aggregate-Action billing ops.
  invoice_add: "invoices", invoice_status_change: "invoices",
  invoice_blocking_note_set: "invoices",
  // Notes — free-text comment / flag.
  comment: "notes", flag_next_week: "notes",
};

/** The menu section a feedback kind renders under. Unmapped kinds fall to
 *  the heading-less "other" bucket so a new/legacy kind is never dropped. */
export function sectionForKind(kind: string): string {
  return SECTION_BY_KIND[kind] ?? "other";
}

// #93 close-suggestion pairing: when a closure_candidate is live, the Close
// verb and its "Keep open (dismiss proposal)" counterpart for the SAME
// entity render adjacently in a highlighted mini-group (both responses to
// the proposal, together). Adjacency, not a two-step control — 0 added
// clicks. Keyed close-kind → its dismiss-kind.
export const CLOSURE_PAIR_BY_CLOSE: Record<string, string> = {
  work_item_close: "work_item_dismiss_closure_candidate",
  // Cascade-close: approving an agent close proposal on an Item that still
  // has open children (Ray's main flow) pairs the CASCADE verb with the same
  // dismiss — without this key the "Close suggested" box dead-ends again.
  work_item_close_cascade: "work_item_dismiss_closure_candidate",
  action_close: "action_dismiss_closure_candidate",
};

// Below this many kinds a menu is already scannable — render it flat (no
// headings) to avoid over-structuring small pickers (sub-category, closed
// tombstones). Category (9) / Item (~14) / Action (~19) / Needs-Input (6)
// clear it and get sectioned.
export const MENU_GROUPING_MIN_KINDS = 6;

// Popover header title — tier-specific so the recipient sees WHAT they're
// giving feedback on ("Item feedback" / "Action feedback") rather than a
// generic "Add feedback" (Ray 6/30 dogfood). The domain nouns
// (Category / Item / Action / Sub-category) are capitalized to match the
// KIND_LABEL house style; "feedback" stays lowercase (sentence case).
export function feedbackPopoverTitle(targetType: TargetType): string {
  switch (targetType) {
    case "thread":
      return "Category feedback";
    case "work_item":
    case "item":
      return "Item feedback";
    case "action_item":
      return "Action feedback";
    case "subcategory":
      return "Sub-category feedback";
    default:
      // project shell / unfiled / informational / legacy tiers.
      return "Add feedback";
  }
}

// One rendered row in a menu group: either a single kind button, or the
// #93 close-suggestion pair (Close + its dismiss counterpart, boxed).
export type MenuRow = { kind: string } | { pair: [string, string] };
export interface MenuGroup {
  /** MENU_SECTIONS id — keys the #107 collapse default + persistence. "" for
   *  the flat / trailing bucket, which has no heading and never collapses. */
  id: string;
  /** Section heading; "" renders no heading (flat menu / trailing bucket). */
  label: string;
  rows: MenuRow[];
}

// #107: sections that stay EXPANDED by default — the everyday path (Status =
// close/reopen, Edit = narrative/priority/…) plus Place (the primary verb on a
// Needs-Your-Input row). The rest default COLLAPSED so a loaded menu rolls up
// (Ray 7/07), but every header is one click either way and nothing is hidden
// permanently (Noah's discoverability caveat). A stored per-section preference
// overrides this default (see menuSectionIsOpen).
const DEFAULT_OPEN_SECTIONS: ReadonlySet<string> = new Set(["status", "edit", "place"]);

/** #107: whether a section is expanded by default (before any stored pref). */
export function defaultSectionOpen(sectionId: string): boolean {
  return DEFAULT_OPEN_SECTIONS.has(sectionId);
}

const MENU_SECTION_PREF_PREFIX = "es_portal_menu_section_";

/** #107: a section's open state — the recipient's stored per-section choice if
 *  they've toggled it (global across menus, so a power user who opens Reorganize
 *  keeps it open everywhere), else the B default. Node-safe (guards localStorage). */
export function menuSectionIsOpen(sectionId: string): boolean {
  try {
    if (typeof localStorage !== "undefined") {
      const v = localStorage.getItem(MENU_SECTION_PREF_PREFIX + sectionId);
      if (v === "open") return true;
      if (v === "collapsed") return false;
    }
  } catch {
    /* private-mode / disabled storage — fall through to the default */
  }
  return defaultSectionOpen(sectionId);
}

/** #107: persist a section's open state so it survives re-opens + sessions. */
export function setMenuSectionOpen(sectionId: string, open: boolean): void {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(MENU_SECTION_PREF_PREFIX + sectionId, open ? "open" : "collapsed");
    }
  } catch {
    /* best-effort; a lost preference is harmless */
  }
}

const MERGE_PROPOSAL_DISMISS_PREFIX = "es_portal_merge_proposal_dismissed_";

/** N1 (2026-07-14): whether the recipient dismissed a specific Item→target merge
 *  suggestion. Session-local (localStorage): the matter consult is surface-only and
 *  re-derives every run, so a persistent/agent-trained dismissal is a follow-up —
 *  this just stops the banner re-nagging within the week. Node-safe. */
export function mergeProposalDismissed(key: string): boolean {
  try {
    if (typeof localStorage !== "undefined") {
      return localStorage.getItem(MERGE_PROPOSAL_DISMISS_PREFIX + key) === "1";
    }
  } catch {
    /* disabled storage — treat as not dismissed */
  }
  return false;
}

/** N1: persist a merge-suggestion dismissal so it doesn't re-appear on re-open. */
export function dismissMergeProposal(key: string): void {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(MERGE_PROPOSAL_DISMISS_PREFIX + key, "1");
    }
  } catch {
    /* best-effort */
  }
}

const PROPOSED_ANSWER_DISMISS_PREFIX = "es_portal_proposed_answer_dismissed_";

/** N2/#117: whether the recipient dismissed a specific proposed answer.
 *  localStorage-only (mirrors the N1 merge-proposal dismiss): the candidate
 *  stays in state (never shown on the download PSU / narrative until accepted),
 *  this just hides the suggestion for this recipient. A durable, cross-device
 *  dismiss is a backlogged follow-up (would need a new wire kind + Worker). */
export function proposedAnswerDismissed(key: string): boolean {
  try {
    if (typeof localStorage !== "undefined") {
      return localStorage.getItem(PROPOSED_ANSWER_DISMISS_PREFIX + key) === "1";
    }
  } catch {
    /* disabled storage — treat as not dismissed */
  }
  return false;
}

/** N2/#117: persist a proposed-answer dismissal so it doesn't re-appear. */
export function dismissProposedAnswer(key: string): void {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(PROPOSED_ANSWER_DISMISS_PREFIX + key, "1");
    }
  } catch {
    /* best-effort */
  }
}

// Pair a live close-suggestion (Close + "Keep open") into one boxed row,
// leaving every other kind as its own row. Order preserved; the dismiss
// kind is consumed where its close partner sits.
function toRows(kinds: string[]): MenuRow[] {
  const rows: MenuRow[] = [];
  const paired = new Set<string>();
  for (const kind of kinds) {
    if (paired.has(kind)) continue;
    const dismiss = CLOSURE_PAIR_BY_CLOSE[kind];
    if (dismiss && kinds.includes(dismiss)) {
      rows.push({ pair: [kind, dismiss] });
      paired.add(kind);
      paired.add(dismiss);
      continue;
    }
    rows.push({ kind });
  }
  return rows;
}

/** Pure layout planner for the feedback picker. Small menus (< MIN kinds)
 *  render flat (one unlabeled group); larger ones split into the ordered
 *  MENU_SECTIONS (only non-empty sections appear). Within-section order is
 *  the input order (the operator-tuned kindsFor*V4 sequence). The DOM
 *  render is a thin consumer of this plan — keeping the grouping logic
 *  node-testable without a DOM. */
export function planMenuLayout(kinds: string[]): MenuGroup[] {
  if (kinds.length < MENU_GROUPING_MIN_KINDS) {
    return [{ id: "", label: "", rows: toRows(kinds) }];
  }
  const groups: MenuGroup[] = [];
  for (const section of MENU_SECTIONS) {
    const inSection = kinds.filter((k) => sectionForKind(k) === section.id);
    if (inSection.length === 0) continue;
    // The heading-less trailing bucket (label "") keeps id "" so the render
    // treats it as flat (no collapse toggle).
    groups.push({ id: section.label ? section.id : "", label: section.label, rows: toRows(inSection) });
  }
  return groups;
}

function helperTextForKind(
  kind: string,
  targetType: FormControlSpec["targetType"],
): string {
  // Scope-specific: missing_item means different things on a Category
  // vs an Item, so split. Round-4 #1: each helper now names the OTHER
  // affordance so the create-now vs describe-for-triage split is explicit.
  if (kind === "missing_item") {
    if (targetType === "thread") {
      return "Describe an Item that should sit under this Category but isn't here. Nothing is created yet — the operator files it. Ready to name it yourself? Use Add Item instead.";
    }
    return "Describe an Action or fact that should sit under this Item but isn't here. Nothing is created yet — the operator triages it.";
  }
  if (kind === "item_add") {
    return "Creates the Item right away — you name it and it appears in the briefing. Only want to describe what's missing? Use Report something missing instead.";
  }
  if (kind === "action_edit_details") {
    return "Edit this Action's details in one place. Only the fields you change are submitted; leave a field as-is to keep it.";
  }
  if (kind === "work_item_close_cascade") {
    return "Closes this Item AND every open Action listed below in one step. Each Action is recorded as closed with the Item.";
  }
  if (kind === "comment") {
    return "Free-form note attached to this entry. Operator-visible; doesn't change the briefing on its own.";
  }
  if (kind === "flag_next_week") {
    return "Asks the operator to surface this in next week's briefing even if nothing new arrives.";
  }
  if (kind === "subcategory_add") {
    return "Create an unnumbered grouping inside this Category (e.g. a contractor or area split). Items keep their numbers; you then move Items into it.";
  }
  if (kind === "subcategory_delete") {
    return "Removes this sub-category grouping. Its Items are NOT closed or lost — they stay in the Category, just un-grouped (their numbers don't change). Use this to undo a grouping you didn't want. Unlike Close, nothing is tombstoned — the group simply goes away.";
  }
  if (kind === "item_move_to_subcategory") {
    return "Group this Item under one of this Category's sub-categories, or choose “(ungrouped)” to take it out of a group. The Item's number doesn't change.";
  }
  if (kind === "item_move") {
    return "Move this Item to a different Category, or group it under one of this Category's sub-categories.";
  }
  if (kind === "category_move_to_phase") {
    return "Place this Category under a different proposal phase (Pre-Development, Pre-Construction, Construction, or General). Grouping only — the Category keeps its number and all its Items. Reflected in next week's briefing.";
  }
  if (kind === "thread_reject") {
    return "Tells the operator this Category is off-topic for the project. The Category is removed and its evidence items are reclassified.";
  }
  // #60 PR4-v2 — the two consolidated placement verbs.
  if (kind === "place_new") {
    return "Create this as a new Category, Item, or Action where it belongs. Tick \"Already complete\" to file it as closed (a done item, not active work). Reflected in next week's briefing.";
  }
  // honest copy — Merge never reopens or adds active work; only the Item tier
  // produces a Closed-Items-visible record (per-field notes scope that claim).
  if (kind === "merge_into_existing") {
    return "For something that's the same as an existing item — folds this into it and stops it reappearing here. It won't show in the weekly briefing, and never reopens or adds active work.";
  }
  // Task 3 — say plainly what each structural action does + when it lands.
  if (kind === "candidate_to_item") {
    return "Creates an Item under the Category you choose, seeded from this suggestion, and removes the suggestion from Needs Your Input. Reflected in next week's briefing.";
  }
  if (kind === "work_item_merge") {
    return "Folds the Items you pick into this one. This Item stays the primary — it keeps its name and description (rename it below if you like); the others' Actions move under it and the merged Items go away (shown as recently-closed for two weeks). Next week's briefing re-describes this Item from the combined work — nothing is blended into the title.";
  }
  if (kind === "item_merge_into") {
    return "Folds THIS Item up into the one you pick — the reverse of “Merge Items”. The picked Item becomes the primary (keeps its name and description); this Item's Actions move under it and this Item goes away (shown as recently-closed for two weeks). Use it when you're looking at a duplicate and want to roll it into the right Item without leaving this row.";
  }
  if (kind === "action_merge") {
    return "Folds the Actions you pick — from this or any other Item — into this one. This Action stays the primary (keep its wording, or rename it below); the others are marked merged with a 'from …' breadcrumb and stay visible as recently-closed for two weeks. Reflected in next week's briefing.";
  }
  if (kind === "action_merge_into") {
    return "Folds THIS Action up into the one you pick — the reverse of “Merge Actions”. The picked Action becomes the primary (keeps its wording); this Action is marked merged with a 'from …' breadcrumb and stays visible as recently-closed for two weeks. Use it when you're looking at a duplicate and want to roll it into the right Action without leaving this row.";
  }
  if (kind === "work_item_rename") {
    return "Changes this Item's display name only (its history is preserved). Reflected in next week's briefing.";
  }
  if (kind === "action_rename") {
    return "Changes this Action's text only (its history is preserved). Reflected in next week's briefing.";
  }
  if (kind === "action_to_item") {
    return "For an Action the agent should have tracked as its own Item. Creates a NEW Item (named below — defaults to this Action's text) under the Category you pick, and closes this Action with a \"promoted to Item\" note (it stays visible in Recently Closed). Use it on assumed/new rows — nothing is double-counted, and the new Item appears in next week's briefing.";
  }
  return "";
}

function describePayload(kind: string, payload: Record<string, unknown>): string {
  const pickStr = (k: string): string | null => {
    const v = payload[k];
    return typeof v === "string" && v.trim() ? v.trim() : null;
  };
  switch (kind) {
    case "responsible_party_assign": {
      const name = pickStr("name");
      return name ? `→ ${name}` : "";
    }
    case "thread_rename": {
      const to = pickStr("to_name");
      return to ? `New name: ${to}` : "";
    }
    case "thread_merge": {
      const tgt = pickStr("target_slug");
      return tgt ? `Merge into: ${tgt}` : "";
    }
    case "thread_retag": {
      const to = pickStr("to_thread");
      return to ? `New thread: ${to}` : "";
    }
    case "item_move_to_category": {
      const to = pickStr("to_thread_slug");
      return to ? `→ ${to}` : "";
    }
    case "item_move_to_subcategory": {
      const to = pickStr("to_subcategory_slug");
      return to ? `→ ${to}` : "→ (ungrouped)";
    }
    case "subcategory_add": {
      return pickStr("name") ?? "";
    }
    case "subcategory_rename": {
      const to = pickStr("to_name");
      return to ? `New name: ${to}` : "";
    }
    case "date_correction": {
      const field = pickStr("field") ?? "date";
      const value = pickStr("new_value") ?? "(not set)";
      return `${field} → ${value}`;
    }
    case "review_clarification": {
      const v = pickStr("verdict");
      return v ? `Verdict: ${v}` : "";
    }
    case "action_priority_change": {
      const np = pickStr("new_priority");
      const due = pickStr("due");
      const reason = pickStr("reason");
      const parts: string[] = [];
      if (np) parts.push(`→ ${np}`);
      if (due) parts.push(`due ${due}`);
      if (reason) parts.push(`reason: ${reason}`);
      return parts.join(", ");
    }
    case "action_set_due": {
      const due = pickStr("due");
      return due ? `due ${due}` : "(cleared)";
    }
    case "action_set_reason": {
      const v = pickStr("reason");
      return v ? `reason: ${v}` : "";
    }
    case "action_set_impact": {
      const v = pickStr("impact");
      return v ? `impact: ${v}` : "";
    }
    case "action_set_off_hold_condition": {
      const v = pickStr("off_hold_condition");
      return v ? `off-hold: ${v}` : "";
    }
    case "action_add": {
      const title = pickStr("text");
      const priority = pickStr("priority_status");
      const due = pickStr("due");
      const meta: string[] = [];
      if (priority) meta.push(priority);
      if (due) meta.push(`due ${due}`);
      const suffix = meta.length ? ` (${meta.join(", ")})` : "";
      return title ? `${title}${suffix}` : "";
    }
    case "item_add":
    case "category_add": {
      return pickStr("name") ?? "";
    }
    case "candidate_to_item": {
      const name = pickStr("name");
      const tgt = pickStr("target_thread_slug");
      const act = pickStr("initial_action_text");
      const parts: string[] = [];
      if (name && tgt) parts.push(`"${name}" → ${tgt}`);
      else if (name) parts.push(`"${name}"`);
      if (act) parts.push(`+ action: ${act}`);
      return parts.join(" ");
    }
    case "work_item_merge": {
      const srcs = payload["source_work_item_slugs"];
      const n = Array.isArray(srcs) ? srcs.length : 0;
      return n ? `merge ${n} Item${n === 1 ? "" : "s"} in` : "";
    }
    case "action_merge": {
      const srcs = payload["source_action_item_slugs"];
      const n = Array.isArray(srcs) ? srcs.length : 0;
      const rename = pickStr("target_text");
      const base = n ? `merge ${n} Action${n === 1 ? "" : "s"} in` : "";
      return rename ? `${base}${base ? ", " : ""}→ "${rename}"` : base;
    }
    case "work_item_rename": {
      const to = pickStr("to_name");
      return to ? `New name: ${to}` : "";
    }
    case "action_rename": {
      const to = pickStr("to_text");
      return to ? `New text: ${to}` : "";
    }
    case "thread_close":
    case "work_item_close":
    case "work_item_close_cascade":
    case "action_close":
    case "thread_reopen":
    case "work_item_reopen":
    case "action_reopen":
    case "subcategory_close":
    case "subcategory_reopen":
    case "subcategory_delete":
    case "thread_reject":
    case "work_item_acknowledge":
    case "action_acknowledge":
    // B2 PR-1: placed not-relevant rejection — optional reason, same shape.
    case "work_item_not_relevant":
    case "action_not_relevant": {
      const reason = pickStr("reason");
      return reason ? `Reason: ${reason}` : "";
    }
    // R2-3: direct narrative edit — show the (truncated) new text.
    case "work_item_set_narrative":
    case "action_set_narrative": {
      const narrative = pickStr("narrative");
      if (!narrative) return "Narrative cleared";
      return narrative.length > 80 ? `${narrative.slice(0, 80)}…` : narrative;
    }
    // R2-3 fast-follow: direct exec-summary edit — show the (truncated) new text.
    case "set_executive_summary": {
      const summary = pickStr("summary");
      if (!summary) return "Summary cleared";
      return summary.length > 80 ? `${summary.slice(0, 80)}…` : summary;
    }
    case "action_open_question_add": {
      const text = pickStr("text");
      return text ? `Q: ${text}` : "";
    }
    case "action_open_question_resolve": {
      const answer = pickStr("answer");
      return answer ? `A: ${answer}` : "";
    }
    case "action_open_question_dismiss": {
      // Show WHICH question was dismissed — the chip is the only place the
      // recipient can still see it, since the question itself is gone from the
      // render the moment this lands.
      const q = pickStr("question_text");
      return q ? `Dismissed: ${q}` : "Dismissed";
    }
    case "action_kil_mark":
    case "action_kil_unmark":
    case "item_kil_mark":
    case "item_kil_unmark":
    case "action_dismiss_closure_candidate":
    case "work_item_dismiss_closure_candidate":
    case "informational_acknowledge":
    case "work_item_track":
    case "action_track":
      // Pure toggles / confirmations — no meaningful payload.
      return "";
  }
  // Generic fallbacks for kinds with a free-text field.
  const text = pickStr("text");
  if (text) return text;
  const note = pickStr("note");
  if (note) return note;
  return "";
}

function relativeTime(iso: string): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return iso;
  const deltaSec = Math.round((Date.now() - then) / 1000);
  if (deltaSec < 30) return "just now";
  if (deltaSec < 60) return `${deltaSec}s ago`;
  if (deltaSec < 3600) return `${Math.round(deltaSec / 60)}m ago`;
  if (deltaSec < 86400) return `${Math.round(deltaSec / 3600)}h ago`;
  return `${Math.round(deltaSec / 86400)}d ago`;
}

const PENCIL_SVG =
  '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<path d="M12 20h9"/>' +
  '<path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>' +
  "</svg>";

export interface PopoverPlacement {
  /** True → dock the menu ABOVE the trigger (CSS `.open-up`). */
  openUp: boolean;
  /** Cap the menu to this px height so it always fits the chosen side and
   *  scrolls internally instead of pushing the page. */
  maxHeight: number;
}

/**
 * #106: place the feedback popover collision-aware so opening a menu on a low
 * item never scrolls the whole briefing.
 *
 * The popover opens DOWN by default (CSS `top: calc(100% + 4px)`). On an item
 * low in the viewport that pushed the menu past the document bottom, forcing the
 * page to scroll. Rule:
 *  - Prefer DOWN. Flip UP only when the menu can't fit below AND there's more
 *    room above (near the top we stay down even if neither side fully fits).
 *  - Cap `maxHeight` to the available space on the chosen side (minus an 8px
 *    gap) so a menu taller than that side scrolls INSIDE itself and can't
 *    overflow either viewport edge — this is what removes the residual
 *    top-clip when a near-full-height menu flips up.
 *
 * All inputs are viewport-relative (getBoundingClientRect / innerHeight).
 */
export function computePopoverPlacement(
  triggerTop: number,
  triggerBottom: number,
  viewportHeight: number,
  menuHeight: number,
  gap = 8,
): PopoverPlacement {
  const spaceBelow = viewportHeight - triggerBottom - gap;
  const spaceAbove = triggerTop - gap;
  const openUp = menuHeight > spaceBelow && spaceAbove > spaceBelow;
  return { openUp, maxHeight: Math.max(0, openUp ? spaceAbove : spaceBelow) };
}

/** Client 2026-08-12 — identifies the ONE question a per-question ✎ acts on. */
export interface QuestionContext { qid: string; text: string }

/** The kinds a per-question ✎ offers. Answering and dismissing are the two
 *  dispositions of a question; both are scoped to a single qid, so neither
 *  form shows a "which question" picker. */
export const QUESTION_SCOPED_KINDS: readonly string[] = [
  "action_open_question_resolve",
  "action_open_question_dismiss",
];

/**
 * Stamp the per-question ✎'s identity onto an outgoing payload.
 *
 * Extracted from the submit handler so the DECISION is node-testable and the
 * DOM render stays a thin consumer — the same split `planMenuLayout` /
 * `sectionForKind` already use, and necessary here because this package has no
 * DOM test environment (`vitest.config.ts` → `environment: "node"`).
 *
 * `qid` is the load-bearing key (#52b): it survives the synthesizer re-wording
 * the question between runs, which the older text key did not. `question_text`
 * rides along only as the absorber's documented fallback for a legacy row
 * minted before qids existed. Mutates in place; returns whether it stamped.
 */
/**
 * What a question form should ASK for, given its kind and whether it is scoped
 * to one question. Node-testable decision behind the `buildForm` switch.
 *
 *  - `"none"`        — dismiss: nothing to type. A dismissal that demanded a
 *                      reason would recreate the friction it removes.
 *  - `"answer-only"` — scoped resolve: the question is already identified.
 *  - `"pick-then-answer"` — unscoped resolve (legacy / no context): the old
 *                      picker path. No caller offers this since the per-question
 *                      ✎ landed, but the branch stays for an older blob whose
 *                      questions carry no qid.
 */
export function questionFormShape(
  kind: string, ctx: QuestionContext | undefined,
): "none" | "answer-only" | "pick-then-answer" {
  if (kind === "action_open_question_dismiss") return "none";
  if (kind !== "action_open_question_resolve") return "pick-then-answer";
  return ctx ? "answer-only" : "pick-then-answer";
}

export function injectQuestionContext(
  payload: Record<string, unknown>,
  wireKind: string,
  ctx: QuestionContext | undefined,
): boolean {
  if (!ctx || !QUESTION_SCOPED_KINDS.includes(wireKind)) return false;
  payload.qid = ctx.qid;
  payload.question_text = ctx.text;
  // A scoped form never renders the legacy positional picker, and an index left
  // over from a prefill would out-rank nothing but could still confuse an
  // absorber fallback. Drop it so qid is unambiguously the key.
  delete payload.question_index;
  return true;
}

export function renderFormControls(spec: FormControlSpec): HTMLElement {
  const wrapper = document.createElement("div");
  wrapper.className = "controls";

  // Local mutable copy so optimistic adds after a submit are visible
  // without a refetch.
  const submitted: SubmittedEvent[] = [...(spec.submitted ?? [])];

  const trigger = document.createElement("button");
  trigger.type = "button";
  trigger.className = "btn-edit";
  trigger.innerHTML = PENCIL_SVG;
  const countBadge = document.createElement("span");
  countBadge.className = "btn-edit-count";
  trigger.appendChild(countBadge);

  function refreshTrigger(): void {
    if (submitted.length > 0) {
      trigger.classList.add("has-feedback");
      trigger.title = `${submitted.length} feedback item${submitted.length === 1 ? "" : "s"}`;
      trigger.setAttribute("aria-label", `Review feedback (${submitted.length})`);
      countBadge.textContent = String(submitted.length);
    } else {
      trigger.classList.remove("has-feedback");
      trigger.title = "Add feedback";
      trigger.setAttribute("aria-label", "Add feedback");
      countBadge.textContent = "";
    }
  }
  refreshTrigger();

  const popoverTitle = feedbackPopoverTitle(spec.targetType);

  const popover = document.createElement("div");
  popover.className = "popover hidden";
  popover.setAttribute("role", "dialog");
  popover.setAttribute("aria-label", popoverTitle);

  const header = document.createElement("div");
  header.className = "popover-header";
  const title = document.createElement("span");
  title.className = "popover-title";
  title.textContent = popoverTitle;
  const closeBtn = document.createElement("button");
  closeBtn.type = "button";
  closeBtn.className = "btn-close";
  closeBtn.title = "Close";
  closeBtn.setAttribute("aria-label", "Close");
  closeBtn.textContent = "×";
  header.appendChild(title);
  header.appendChild(closeBtn);

  const body = document.createElement("div");
  body.className = "popover-body";

  popover.appendChild(header);
  popover.appendChild(body);

  let outsideHandler: ((e: MouseEvent) => void) | null = null;
  let escHandler: ((e: KeyboardEvent) => void) | null = null;

  function close(): void {
    popover.classList.add("hidden");
    body.innerHTML = "";
    if (outsideHandler) {
      document.removeEventListener("click", outsideHandler, true);
      outsideHandler = null;
    }
    if (escHandler) {
      document.removeEventListener("keydown", escHandler);
      escHandler = null;
    }
  }

  function showPicker(): void {
    body.innerHTML = "";

    // N1 (2026-07-14): agent merge-suggestion proposal banner at the TOP of the
    // menu — an orange-bounded Accept/Dismiss, mirroring the closure_candidate
    // affordance. Renders ABOVE the #107 sections (composes with the roll-up).
    // Accept opens the pre-filled item_merge_into form (target already selected
    // via spec.current.merge_primary_item → confirm); Dismiss hides it for the
    // session (the surface-only matter hint re-derives next run).
    const targets = spec.mergeProposal?.targets ?? [];
    const primary = targets[0];
    if (primary) {
      // Item-level key: dismissing hides this Item's whole merge proposal (banner
      // + inline note) for the session, regardless of how many candidates it named.
      // #66 D1-D3: overlap-bearing proposals carry a distinct key (briefings.ts
      // mergeBannerDismissKey) so a new overlap resurfaces past an old dismissal.
      const dismissKey = spec.mergeProposalDismissKey ?? spec.targetId;
      if (!mergeProposalDismissed(dismissKey)) {
        const banner = document.createElement("section");
        banner.className = "proposal-banner";
        const msg = document.createElement("p");
        msg.className = "proposal-banner-msg";
        const lead = document.createElement("span");
        lead.textContent = "Agent suggests merging this Item into ";
        msg.appendChild(lead);
        // One candidate → a bold label; several → a dropdown (default the first)
        // so the recipient picks WHICH to fold into before accepting.
        let getSelectedSlug: () => string;
        if (targets.length === 1) {
          const strong = document.createElement("strong");
          strong.textContent = primary.label;
          msg.appendChild(strong);
          getSelectedSlug = () => primary.slug;
        } else {
          const sel = document.createElement("select");
          sel.className = "proposal-target-select";
          for (const t of targets) {
            const opt = document.createElement("option");
            opt.value = t.slug;
            opt.textContent = t.label;
            sel.appendChild(opt);
          }
          sel.value = primary.slug;
          msg.appendChild(sel);
          getSelectedSlug = () => sel.value;
        }
        banner.appendChild(msg);
        // #66 D1-D3: the overlap tell as a muted context line ("Narrative
        // notes an overlap with …") so the recipient sees WHY before acting.
        if (spec.mergeProposal?.reason) {
          const why = document.createElement("p");
          why.className = "proposal-banner-reason";
          why.textContent = spec.mergeProposal.reason;
          banner.appendChild(why);
        }
        const actions = document.createElement("div");
        actions.className = "proposal-banner-actions";
        const accept = document.createElement("button");
        accept.type = "button";
        accept.className = "btn-action proposal-accept";
        // #66 D1-D3 review gate: folding a multi-action Item on an overlap
        // signal opens the prefilled form for review instead of auto-submitting
        // (the accept merges EVERY action the Item carries — show scope first).
        const reviewMode = Boolean(spec.mergeProposal?.review);
        accept.textContent = reviewMode ? "Review & merge…" : "Accept & merge";
        accept.addEventListener("click", () => {
          // One-click accept: build the item_merge_into form pre-targeted to the
          // chosen primary and submit it programmatically — reuses the EXACT
          // tested submit handler (FormData → reshape → resolveItemMergeInto →
          // work_item_merge → optimistic fold) with NO second click, NO flash.
          // Two things make a hidden programmatic submit reliable:
          //  • `noValidate` — a display:none form aborts requestSubmit()'s
          //    constraint validation on the non-focusable <select> even when it's
          //    valid; skipping validation lets the submit event fire. (The value
          //    is set explicitly below, so we don't need the browser's check.)
          //  • set the <select> value directly — belt-and-suspenders so the
          //    FormData carries the chosen primary regardless of pre-fill timing.
          const chosen = getSelectedSlug();
          spec.current = {
            ...(spec.current ?? {}),
            merge_primary_item: chosen,
            // #66 D4: tag the accept so it can be joined back to the proposal.
            ...(spec.mergeProposal?.proposalId
              ? { from_proposal: spec.mergeProposal.proposalId } : {}),
          };
          const form = buildForm(
            spec, "item_merge_into", onSubmitted, showPicker,
          ) as HTMLFormElement;
          const sel = form.querySelector<HTMLSelectElement>(
            'select[name="merge_primary_item"]',
          );
          if (sel) sel.value = chosen;
          body.innerHTML = "";
          body.appendChild(form);
          if (reviewMode) {
            // Visible prefilled form — the recipient confirms via its own
            // submit (scope guard for a multi-action whole-Item fold).
            return;
          }
          form.noValidate = true;
          form.hidden = true;
          form.requestSubmit();
        });
        const dismiss = document.createElement("button");
        dismiss.type = "button";
        dismiss.className = "btn-action proposal-dismiss";
        dismiss.textContent = "Dismiss";
        dismiss.addEventListener("click", () => {
          dismissMergeProposal(dismissKey);
          // #66 D4: ALSO record the rejection durably. The localStorage flag
          // above only stops the banner re-nagging in THIS browser this week —
          // the hint is re-derived from the tree every run, and the recipient
          // reviews across devices, so without a server-side record the same
          // rejected proposal returns next week on a fresh device. Submitted
          // through the normal form path (hidden + programmatic, exactly like
          // Accept) so it goes through the SAME tested reshape → submit code.
          // Best-effort: a failure here must not block the visual dismiss the
          // recipient just asked for.
          const rejected = getSelectedSlug();
          // Only an OVERLAP-sourced target earns a durable row — see
          // durableDismissSlugs. A matter-ledger suggestion is dismissed
          // locally, exactly as it was before D4.
          const durable = spec.mergeProposal?.durableDismissSlugs ?? [];
          if (rejected && durable.includes(rejected)) {
            try {
              const prior = spec.current;
              spec.current = { ...(prior ?? {}), target_work_item_slug: rejected };
              const dismissForm = buildForm(
                spec, "work_item_dismiss_merge_candidate",
                onSubmitted, showPicker,
              ) as HTMLFormElement;
              spec.current = prior;
              dismissForm.noValidate = true;
              dismissForm.hidden = true;
              // Into the popover body — the same place Accept puts its
              // programmatic form. document.body works for the submit itself
              // but sits OUTSIDE the popover, and each dismiss would leave a
              // hidden form behind forever. Removed after submit either way.
              body.appendChild(dismissForm);
              dismissForm.requestSubmit();
              dismissForm.remove();
            } catch {
              /* durable record is best-effort; the local dismiss still stands */
            }
          }
          // operator 2026-07-14: dismissing clears the inline "◇ Possibly part
          // of…" note on the Item title too, not just the banner. The briefing
          // doesn't re-render on dismiss, so remove it directly here; the
          // render-time mergeProposalDismissed guard (briefings.ts) keeps it gone
          // on any later re-render / reload this session. #66 D1-D3: an Item can
          // carry BOTH a matter note and an overlap note — clear ALL of them
          // (action-row overlap notes use a different class, `action-overlap-
          // note`, and are inline-only advisories with no dismiss affordance).
          body.closest(".work-item")
            ?.querySelectorAll(".item-matter-note")
            .forEach((el) => el.remove());
          showPicker();
        });
        actions.append(accept, dismiss);
        banner.appendChild(actions);
        body.appendChild(banner);
      }
    }

    if (submitted.length > 0) {
      const history = document.createElement("section");
      history.className = "popover-history";
      const heading = document.createElement("h3");
      heading.className = "popover-section-heading";
      heading.textContent = "Submitted";
      history.appendChild(heading);

      // Lifecycle hint: recipient feedback sits in the Worker's pending
      // queue until the next absorb-feedback run (driven by the weekly
      // cron). Surfacing this is the difference between "nothing
      // happened" and "received, will land next week" — Ray's 2026-05-28
      // session flagged the gap explicitly ("not clear what happens
      // after I add feedback").
      const lifecycleNote = document.createElement("p");
      lifecycleNote.className = "popover-history-note";
      lifecycleNote.textContent =
        "Pending — these changes will be reflected in next week's briefing.";
      history.appendChild(lifecycleNote);

      const ul = document.createElement("ul");
      ul.className = "popover-history-list";
      const ordered = [...submitted].sort((a, b) =>
        a.submitted_at < b.submitted_at ? 1 : a.submitted_at > b.submitted_at ? -1 : 0,
      );
      for (const ev of ordered) {
        const li = document.createElement("li");
        li.className = "popover-history-item";
        // Top line: kind label on the left, Undo control pinned top-right.
        const topline = document.createElement("div");
        topline.className = "popover-history-topline";
        const head = document.createElement("div");
        head.className = "popover-history-kind";
        head.textContent = KIND_LABEL[ev.kind] ?? ev.kind;
        topline.appendChild(head);
        // B7: Undo a still-pending event (not yet drained by poll-portal).
        // Hidden once absorbed — the inverse correction (Reopen / rename back)
        // is the path then. `pending !== false` keeps it shown for legacy /
        // fixture rows that omit the flag; the Worker 409s if it's wrong.
        // Two-step (Undo → Confirm/Cancel) so a stray click can't undo.
        if (ev.pending !== false) {
          topline.appendChild(buildUndoControl(ev, li));
        }
        li.appendChild(topline);
        const preview = describePayload(ev.kind, ev.payload);
        if (preview) {
          const p = document.createElement("div");
          p.className = "popover-history-preview";
          p.textContent = preview;
          li.appendChild(p);
        }
        const ts = document.createElement("div");
        ts.className = "popover-history-ts";
        ts.textContent = relativeTime(ev.submitted_at);
        ts.title = ev.submitted_at;
        li.appendChild(ts);
        ul.appendChild(li);
      }
      history.appendChild(ul);
      body.appendChild(history);

      const sectionHeading = document.createElement("h3");
      sectionHeading.className = "popover-section-heading";
      sectionHeading.textContent = "Add another";
      body.appendChild(sectionHeading);
    }

    // Slice 7 → Ray 6/30 dogfood: the "Close N open items first" advisory
    // no longer renders as an always-on banner at the top of the picker
    // (it read as noise). It stays on the gated close button's hover
    // tooltip, and expands inline UNDER that button if the recipient
    // clicks the greyed-out button anyway (see makeKindButton).

    const makeKindButton = (kind: string): HTMLButtonElement => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "btn-action";
      // Decision 3B (close-out 2026-05-25): "Add X" vs "Edit X" based
      // on whether the field is populated on the Action target. Same
      // kind submitted regardless — label-only change to help the
      // client see whether their click will fill a blank or edit
      // existing text. Driven by spec.populatedFields.
      btn.textContent = labelForKind(kind, spec.populatedFields);
      // A gated close kind (thread_close with open Items, work_item_close
      // with open Actions, #38 subcategory_close with open members — B9):
      // the Worker would 409 the close. It renders greyed with the reason
      // on hover; clicking the greyed button toggles the reason INLINE
      // under it (instead of the old always-on banner), and never opens
      // the reject-bound form.
      const gated = Boolean(spec.notice) && (kind === "thread_close"
        || kind === "work_item_close" || kind === "subcategory_close");
      if (gated) {
        btn.classList.add("btn-action-disabled");
        btn.title = spec.notice!;
        btn.setAttribute("aria-disabled", "true");
        btn.addEventListener("click", () => {
          const sib = btn.nextElementSibling;
          if (sib && sib.classList.contains("popover-gate-reason")) {
            sib.remove();
            return;
          }
          const reason = document.createElement("p");
          reason.className = "popover-gate-reason";
          reason.textContent = spec.notice!;
          btn.insertAdjacentElement("afterend", reason);
        });
      } else {
        btn.addEventListener("click", () => showForm(kind as FeedbackKind));
      }
      return btn;
    };

    // #93 close-suggestion pair — Close + "Keep open (dismiss proposal)"
    // boxed together and highlighted so both responses to the proposal sit
    // adjacent (Ray couldn't find keep-open in the flat wall).
    const makeCloseSuggestion = (close: string, dismiss: string): HTMLElement => {
      const group = document.createElement("div");
      group.className = "popover-close-suggestion";
      const label = document.createElement("div");
      label.className = "popover-close-suggestion-label";
      label.textContent = "Close suggested";
      group.appendChild(label);
      group.appendChild(makeKindButton(close));
      group.appendChild(makeKindButton(dismiss));
      return group;
    };

    const list = document.createElement("div");
    list.className = "popover-actions";
    const rowEl = (row: MenuRow): HTMLElement =>
      "pair" in row
        ? makeCloseSuggestion(row.pair[0], row.pair[1])
        : makeKindButton(row.kind);

    // R2 (2026-07): thin DOM consumer of the pure planMenuLayout — labeled
    // section groups for a loaded menu, flat for small ones (one "" group).
    // #107: labeled sections are collapsible roll-ups — a toggle header (chevron
    // + count) over a container we hide when collapsed. Default open/closed comes
    // from menuSectionIsOpen (B defaults + the recipient's stored per-section
    // choice); toggling persists it. The flat "" bucket never collapses.
    for (const group of planMenuLayout(spec.kinds)) {
      if (!group.label) {
        for (const row of group.rows) list.appendChild(rowEl(row));
        continue;
      }

      const kindCount = group.rows.reduce((n, r) => n + ("pair" in r ? 2 : 1), 0);
      let open = menuSectionIsOpen(group.id);

      const heading = document.createElement("button");
      heading.type = "button";
      heading.className = "popover-group-heading";
      heading.setAttribute("aria-expanded", String(open));

      const chevron = document.createElement("span");
      chevron.className = "popover-group-chevron";
      chevron.setAttribute("aria-hidden", "true");
      const labelEl = document.createElement("span");
      labelEl.className = "popover-group-label";
      labelEl.textContent = group.label;
      const countEl = document.createElement("span");
      countEl.className = "popover-group-count";
      countEl.textContent = String(kindCount);
      heading.append(chevron, labelEl, countEl);
      list.appendChild(heading);

      const container = document.createElement("div");
      container.className = "popover-group";
      for (const row of group.rows) container.appendChild(rowEl(row));
      list.appendChild(container);

      const applyOpen = (): void => {
        container.hidden = !open;
        heading.setAttribute("aria-expanded", String(open));
        heading.classList.toggle("collapsed", !open);
      };
      applyOpen();
      heading.addEventListener("click", () => {
        open = !open;
        applyOpen();
        setMenuSectionOpen(group.id, open);
      });
    }
    body.appendChild(list);
  }

  function showForm(kind: FeedbackKind): void {
    body.innerHTML = "";
    body.appendChild(buildForm(spec, kind, onSubmitted, showPicker));
  }

  function onSubmitted(event: SubmittedEvent): void {
    submitted.push(event);
    refreshTrigger();
    showPicker();
  }

  // B7: the per-row Undo control — a two-step affordance so a stray click
  // can't undo. Renders an "Undo" trigger; clicking it swaps to a
  // Confirm/Cancel pair (Cancel restores the trigger). Confirm runs the
  // actual undo: DELETE the event from the Worker, drop it from the local
  // Submitted list, and bubble `feedback-retracted` so the viewer (app.ts)
  // reverts the shared feedback array + optimistic payload. Edit = undo +
  // resubmit; there is no edit kind.
  function buildUndoControl(ev: SubmittedEvent, li: HTMLElement): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = "popover-history-undo";

    const trigger = document.createElement("button");
    trigger.type = "button";
    trigger.className = "popover-history-undo-trigger";
    trigger.textContent = "Undo";
    trigger.title = "Undo this pending change";
    trigger.addEventListener("click", arm);

    function reset(): void {
      wrap.innerHTML = "";
      wrap.appendChild(trigger);
    }

    function arm(): void {
      wrap.innerHTML = "";
      const confirm = document.createElement("button");
      confirm.type = "button";
      confirm.className = "popover-history-undo-confirm";
      confirm.textContent = "Confirm";
      confirm.title = "Confirm undo";
      confirm.addEventListener("click", () => doUndo(confirm));
      const cancel = document.createElement("button");
      cancel.type = "button";
      cancel.className = "popover-history-undo-cancel";
      cancel.textContent = "Cancel";
      cancel.addEventListener("click", reset);
      wrap.appendChild(confirm);
      wrap.appendChild(cancel);
    }

    async function doUndo(confirmBtn: HTMLButtonElement): Promise<void> {
      confirmBtn.disabled = true;
      confirmBtn.textContent = "Undoing…";
      try {
        // Fixture mode: no Worker (DELETE 501s on the static server) — skip
        // the network call and exercise the optimistic local removal + revert.
        if (!inFixtureMode()) {
          await deleteJson<{ retracted: string }>(
            `/feedback/${encodeURIComponent(spec.runId)}/${encodeURIComponent(ev.event_id)}`,
          );
        }
        const idx = submitted.findIndex((e) => e.event_id === ev.event_id);
        if (idx !== -1) submitted.splice(idx, 1);
        document.dispatchEvent(
          new CustomEvent("feedback-retracted", {
            detail: {
              event_id: ev.event_id,
              kind: ev.kind,
              targetId: spec.targetId,
            },
          }),
        );
        refreshTrigger();
        showPicker(); // rebuild the list (+ the "Add another" header if empty)
      } catch (err) {
        // 409 carries a server message: either "already absorbed" (poll-portal
        // drained it between list + delete) or #64's dependent-close guard
        // ("retract the Item/Category close first"). Prefer the server's
        // wording — the two cases need different recipient instructions.
        reset();
        const note = document.createElement("div");
        note.className = "popover-history-undo-note";
        const serverMessage =
          err instanceof ApiError
          && typeof (err.body as { message?: unknown } | null)?.message === "string"
            ? ((err.body as { message: string }).message)
            : null;
        note.textContent = serverMessage
          ?? (err instanceof ApiError && err.status === 409
            ? "Already applied — reverse it with the matching action (e.g. Reopen)."
            : "Couldn't undo — try again.");
        li.appendChild(note);
      }
    }

    reset();
    return wrap;
  }

  function open(): void {
    popover.classList.remove("open-up");
    popover.style.maxHeight = "";
    popover.classList.remove("hidden");
    showPicker();
    // #106: collision-aware placement. Measure the trigger + the now-rendered
    // menu, then flip UP and/or cap the height so the menu always fits its side
    // of the viewport and scrolls internally — never pushing the page. See
    // computePopoverPlacement for the rule + tests.
    const rect = trigger.getBoundingClientRect();
    const placement = computePopoverPlacement(
      rect.top, rect.bottom, window.innerHeight, popover.offsetHeight);
    if (placement.openUp) popover.classList.add("open-up");
    popover.style.maxHeight = `${placement.maxHeight}px`;
    // Register closers on the next tick so the click that opened the
    // popover doesn't immediately close it.
    setTimeout(() => {
      outsideHandler = (ev: MouseEvent) => {
        if (popover.contains(ev.target as Node)) return;
        if (ev.target === trigger || trigger.contains(ev.target as Node)) return;
        close();
      };
      escHandler = (ev: KeyboardEvent) => {
        if (ev.key === "Escape") close();
      };
      document.addEventListener("click", outsideHandler, true);
      document.addEventListener("keydown", escHandler);
    }, 0);
  }

  function toggle(e: Event): void {
    e.stopPropagation();
    if (popover.classList.contains("hidden")) open();
    else close();
  }

  trigger.addEventListener("click", toggle);
  closeBtn.addEventListener("click", close);

  wrapper.appendChild(trigger);
  wrapper.appendChild(popover);
  return wrapper;
}

function buildForm(
  spec: FormControlSpec,
  kind: FeedbackKind,
  onSuccess: (event: SubmittedEvent) => void,
  onBack: () => void,
): HTMLElement {
  const form = document.createElement("form");
  form.className = "feedback-form";
  form.dataset.targetId = spec.targetId;
  form.dataset.kind = kind;

  const heading = document.createElement("p");
  heading.className = "form-heading";
  heading.textContent = KIND_LABEL[kind] ?? kind;
  form.appendChild(heading);

  // Per-kind helper text — addresses Ray's 2026-05-28 confusion on
  // missing_item / comment / flag_next_week ("not sure what this is
  // for"). One sentence; tells the recipient what the operator will
  // see and when it will land. Returns "" when the kind is
  // self-explanatory.
  const helper = helperTextForKind(kind, spec.targetType);
  if (helper) {
    const help = document.createElement("p");
    help.className = "form-helper";
    help.textContent = helper;
    form.appendChild(help);
  }

  // Cascade-close: read-only confirmation list of the open children this
  // submit will close alongside the Item — the recipient sees the full scope
  // before confirming (no silent cascade).
  if (kind === "work_item_close_cascade" && (spec.cascadeOpenActions ?? []).length) {
    const list = document.createElement("ul");
    list.className = "cascade-close-list";
    for (const child of spec.cascadeOpenActions!) {
      const li = document.createElement("li");
      li.textContent = child.label;
      list.appendChild(li);
    }
    form.appendChild(list);
  }

  // Decision 8 (close-out 2026-05-25): `select` widget for enum fields
  // (priority status, invoice status). Decision 3C: `visibleWhen`
  // conditional reveal so the combined action_priority_change form
  // can show reason / impact / off_hold_condition only when the
  // selected priority requires them.
  type FieldSpec = {
    name: string;
    label: string;
    type: "text" | "textarea" | "date" | "select" | "checkbox" | "multiselect"
      // #66 D4: a value the caller already knows, on a form only ever built
      // programmatically. Renders <input type="hidden"> with NO label row, so
      // it carries through FormData without occupying popover space.
      | "hidden";
    required?: boolean;
    /** Pre-filled initial value (edit forms — show the current value to edit). */
    value?: string;
    options?: Array<{ value: string; label: string }>;
    /**
     * When set, this field renders hidden until the value of
     * `visibleWhen.field` matches one of `visibleWhen.equals`. Used
     * for combined-form conditional reveals (Decision 3C).
     */
    visibleWhen?: { field: string; equals: readonly string[] };
    /** B10: render the input disabled (e.g. the "Also close" checkbox when
     *  the Item still has open Actions) with an optional explanatory note. */
    disabled?: boolean;
    note?: string;
  };
  const fields: FieldSpec[] = [];

  switch (kind) {
    case "thread_approve":
    case "thread_confirm":
    case "not_relevant":
    case "flag_next_week":
      fields.push({ name: "note", label: "Note (optional)", type: "textarea" });
      break;
    case "thread_promote":
      // A³ (2026-06-03): the recipient may rename the candidate while
      // promoting it. Pre-filled with the suggested name (spec.current.name).
      fields.push({
        name: "name", label: "Category name", type: "text", required: true,
      });
      break;
    case "thread_reject":
      fields.push({
        name: "reason",
        label: "Reason (optional — e.g. 'off-topic', 'wrong project')",
        type: "textarea",
      });
      fields.push({
        name: "evidence_item_ids",
        label: "Evidence item IDs (optional, comma-separated). Each ID also gets reclassified as 'rejected' for this project.",
        type: "text",
      });
      break;
    case "responsible_party_assign":
      fields.push({
        name: "name",
        label: "Owner name",
        type: "text",
        required: true,
      });
      fields.push({
        name: "email",
        label: "Email (optional)",
        type: "text",
      });
      fields.push({
        name: "note",
        label: "Note (optional — context for future operators)",
        type: "textarea",
      });
      fields.push({
        name: "is_new_person",
        label: "Add to project roster (so they're recognized in future briefings)",
        type: "checkbox",
      });
      break;
    case "comment":
      fields.push({
        name: "text",
        label: "Comment",
        type: "textarea",
        required: true,
      });
      break;
    case "thread_rename":
      fields.push({
        name: "new_name",
        label: "New name",
        type: "text",
        required: true,
      });
      break;
    case "thread_merge":
      fields.push({
        name: "merge_into",
        label: "Merge into Category",
        type: "text",
        required: true,
      });
      break;
    // Decision 2 (close-out): thread_retag, review_clarification, and
    // date_correction are operator-only — removed from the SPA. The
    // type union + absorber + CLI paths stay so existing corrections
    // and direct CLI use continue to work.
    case "missing_item":
      fields.push({
        name: "text",
        label: "What's missing?",
        type: "textarea",
        required: true,
      });
      break;
    case "thread_close":
    case "work_item_close":
    // Cascade-close: same optional reason (rides the ITEM close only; children
    // carry a fixed "Closed with Item <N>" marker — see the submit pre-loop).
    case "work_item_close_cascade":
    case "action_close":
      // Decision 12.8: action_close uses the same reason field as
      // work_item_close. Pre-fills NOTHING — if the recipient is
      // approving a closure_candidate, the candidate's reason is
      // already on record from synth; the optional reason field
      // here captures any *additional* note (e.g. "client confirmed
      // on call 5/30").
      fields.push({
        name: "reason",
        label: "Reason (optional — e.g. 'completed', 'scope cut')",
        type: "textarea",
      });
      break;
    case "thread_reopen":
    case "work_item_reopen":
    case "action_reopen":
      fields.push({
        name: "reason",
        label: "Reason (optional)",
        type: "textarea",
      });
      break;
    case "work_item_acknowledge":
    case "action_acknowledge":
      // B2: acknowledge ("seen, in-project, NOT tracked") — drops the row into
      // the Closed Archive's "Acknowledged / Not Tracked" section, NEVER
      // recorded as completed work. The optional note captures the *why* for
      // the determination log (e.g. "FYI only", "handled off-system").
      fields.push({
        name: "reason",
        label: "Note (optional — why is this not tracked?)",
        type: "textarea",
      });
      break;
    case "work_item_not_relevant":
    case "action_not_relevant":
      // B2 PR-1: "not relevant" (not this project) — a rejection. Drops the row
      // from the briefing for good (not archived, not resurfaceable) and records
      // a `rejected` training signal. The optional note captures the *why*
      // (e.g. "belongs to a different project", "spam").
      fields.push({
        name: "reason",
        label: "Note (optional — why isn’t this relevant?)",
        type: "textarea",
      });
      break;
    case "work_item_set_narrative":
    case "action_set_narrative":
      // R2-3: direct narrative edit — prefilled with the current narrative
      // (PREFILL_KINDS / spec.current.narrative) so the recipient edits what's
      // there. The verbatim text is reasserted every run (overrides the synth +
      // the agent pin). Blank submit clears the narrative.
      fields.push({
        name: "narrative",
        label: "Narrative",
        type: "textarea",
      });
      break;
    case "set_executive_summary":
      // R2-3 fast-follow: direct exec-summary edit — prefilled with the current
      // summary (PREFILL_KINDS / spec.current.summary) so the recipient edits
      // what's there. Reasserted every run (overrides the synth's §14 summary).
      // Blank submit clears it.
      fields.push({
        name: "summary",
        label: "Executive summary",
        type: "textarea",
      });
      break;
    case "action_dismiss_closure_candidate":
    // B2 Track-this: a one-click confirmation — the absorber needs only the
    // target (derived from targetId); it flips acknowledged -> open + deletes
    // the entity_acknowledged row.
    case "work_item_track":
    case "action_track":
      // No fields — this is a confirmation-style action. The form
      // submits with an empty payload; the absorber needs only the
      // target ai_slug (already derived from targetId).
      break;

    // --- v4 (Task #9) Action priority + metadata --------------------------
    // Decision 3C: combined form. Selecting a new priority reveals
    // exactly the metadata fields that priority requires. Selecting
    // Active hides everything. Decision 8: priority is a <select>,
    // not free-text — clients can't typo "Delay potential" anymore.
    case "action_priority_change":
      fields.push({
        name: "new_priority",
        label: "New priority",
        type: "select",
        required: true,
        options: [
          { value: "Critical", label: "Critical" },
          { value: "Delay Potential", label: "Delay Potential" },
          { value: "Active", label: "Active" },
          { value: "On Hold", label: "On Hold" },
        ],
      });
      fields.push({
        name: "reason",
        label: "Reason",
        type: "textarea",
        required: true,
        visibleWhen: {
          field: "new_priority",
          equals: ["Critical", "Delay Potential", "On Hold"],
        },
      });
      fields.push({
        name: "impact",
        label: "Impact",
        type: "textarea",
        required: true,
        visibleWhen: {
          field: "new_priority",
          equals: ["Critical", "Delay Potential"],
        },
      });
      fields.push({
        name: "off_hold_condition",
        label: "Off-hold condition",
        type: "textarea",
        required: true,
        visibleWhen: {
          field: "new_priority",
          equals: ["On Hold"],
        },
      });
      fields.push({
        name: "due",
        label: "Due date",
        type: "date",
        visibleWhen: {
          field: "new_priority",
          equals: ["Critical", "Delay Potential", "Active"],
        },
      });
      break;
    case "action_set_reason":
      fields.push({
        name: "reason", label: "Reason", type: "textarea", required: true,
      });
      break;
    case "action_set_impact":
      fields.push({
        name: "impact", label: "Impact", type: "textarea", required: true,
      });
      break;
    case "action_set_off_hold_condition":
      fields.push({
        name: "off_hold_condition", label: "Off-hold condition",
        type: "textarea", required: true,
      });
      break;
    case "action_set_due":
      fields.push({
        name: "due", label: "Due date (leave blank to clear)", type: "date",
      });
      break;
    case "action_edit_details": {
      // Round-4 #2: ONE form for the priority-relevant metadata fields
      // (prefilled via PREFILL_KINDS from spec.current). None required —
      // the recipient edits a subset; collectActionDetailEdits submits only
      // what changed, as the per-field wire kinds. Same field set as the
      // kindsForActionV4 metaByPriority map; unknown priority shows all four
      // (the pre-Decision-3A defensive posture).
      const p = spec.priorityStatus;
      const showReason = p !== "Active";
      const showImpact = p === "Critical" || p === "Delay Potential" || !p;
      const showOffHold = p === "On Hold" || !p;
      const showDue = p !== "On Hold";
      if (showReason) {
        fields.push({ name: "reason", label: "Reason", type: "textarea" });
      }
      if (showImpact) {
        fields.push({ name: "impact", label: "Impact", type: "textarea" });
      }
      if (showOffHold) {
        fields.push({
          name: "off_hold_condition", label: "Off-hold condition",
          type: "textarea",
        });
      }
      if (showDue) {
        fields.push({
          name: "due", label: "Due date (leave blank to clear)", type: "date",
        });
      }
      break;
    }

    // --- v4 Open questions ---------------------------------------------
    case "action_open_question_add":
      fields.push({
        name: "text", label: "Question", type: "textarea", required: true,
      });
      fields.push({
        name: "raised_by", label: "Raised by (optional)", type: "text",
      });
      break;
    case "action_open_question_dismiss":
      // questionFormShape(kind, ...) === "none".
      // Client 2026-08-12: dispose of a question without answering it. Ray had
      // no way to clear one he judged irrelevant — this form will not submit
      // empty — so he typed "Do not track" into three, fabricating an answer
      // that the agent then fed to the narrative judge as his own words.
      //
      // NO FIELDS. Offered only from the per-question ✎, so `questionContext`
      // already identifies the target and the submit handler injects the qid.
      // There is deliberately nothing to type: a dismissal that asked for a
      // reason would recreate the friction it exists to remove.
      break;

    case "action_open_question_resolve": {
      // Scoped to ONE question (the per-question ✎) — no picker, because the
      // question is on screen and `questionContext` carries its qid.
      if (questionFormShape(kind, spec.questionContext) === "answer-only") {
        fields.push({
          name: "answer", label: "Answer", type: "textarea", required: true,
        });
        break;
      }
      // #52: identify the question by its TEXT (stable across the synth's
      // run-to-run re-derivation), not a positional index. A <select> shows what
      // is being resolved; value = the question text itself. Falls back to a
      // free-text field if the spec didn't carry the questions.
      const oqOpts = spec.openQuestionOptions ?? [];
      if (oqOpts.length > 0) {
        fields.push({
          name: "question_text", label: "Which question",
          type: "select", options: oqOpts, required: true,
        });
      } else {
        fields.push({
          name: "question_text", label: "Question", type: "text", required: true,
        });
      }
      fields.push({
        name: "answer", label: "Answer", type: "textarea", required: true,
      });
      break;
    }

    // --- v4 KIL marks (no fields — instant toggle) ----------------------
    case "action_kil_mark":
    case "action_kil_unmark":
    case "item_kil_mark":
    case "item_kil_unmark":
      // Pure toggle; no fields. The form's submit still emits an
      // empty payload, which is fine — the absorber doesn't read
      // payload on these kinds.
      break;

    // --- v4 Add affordances --------------------------------------------
    // Decision 7B (close-out 2026-05-25): slug fields removed.
    // The client should never see a kebab-case slug. `reshapePayload-
    // ForKind` derives slugs from `name`/`text` via `slugify()`;
    // collisions get a numeric suffix server-side.
    case "item_add":
      fields.push({
        name: "name", label: "Item name", type: "text", required: true,
      });
      fields.push({
        name: "narrative", label: "Narrative (2-3 lines)", type: "textarea",
      });
      fields.push({
        name: "responsible_party_name",
        label: "Responsible (defaults to project PM)", type: "text",
      });
      fields.push({
        name: "start_date",
        label: "Start date (defaults to today)", type: "date",
      });
      break;
    case "category_add":
      fields.push({
        name: "name", label: "Category name", type: "text", required: true,
      });
      fields.push({
        name: "description", label: "Description (optional)", type: "textarea",
      });
      break;
    case "action_add":
      fields.push({
        name: "text", label: "Action title", type: "text", required: true,
      });
      fields.push({
        name: "priority_status",
        label: "Priority",
        type: "select",
        options: [
          { value: "Active", label: "Active" },
          { value: "Critical", label: "Critical" },
          { value: "Delay Potential", label: "Delay Potential" },
          { value: "On Hold", label: "On Hold" },
        ],
      });
      fields.push({
        name: "narrative", label: "Narrative (1-2 lines)", type: "textarea",
      });
      fields.push({
        name: "reason", label: "Reason", type: "textarea",
        visibleWhen: {
          field: "priority_status",
          equals: ["Critical", "Delay Potential", "On Hold"],
        },
      });
      fields.push({
        name: "impact", label: "Impact", type: "textarea",
        visibleWhen: {
          field: "priority_status",
          equals: ["Critical", "Delay Potential"],
        },
      });
      fields.push({
        name: "off_hold_condition",
        label: "Off-hold condition", type: "textarea",
        visibleWhen: {
          field: "priority_status", equals: ["On Hold"],
        },
      });
      fields.push({
        name: "due", label: "Due date", type: "date",
        visibleWhen: {
          field: "priority_status",
          equals: ["Active", "Critical", "Delay Potential"],
        },
      });
      fields.push({
        name: "assignee", label: "Assignee (optional)", type: "text",
      });
      break;

    // --- #60 PR4-v2: the two consolidated placement verbs ---------------
    // "Place as New" — one button, a tier select reveals that tier's real
    // form (Category = name+desc; Item = parent Category + name + narrative +
    // required first Action + priority, Decision 12.5; Action = parent Item +
    // text + priority). The "Already complete" checkbox makes it born-closed.
    // name/text prefill from the row (set after build from bucketContext).
    // Resolves to the real wire kind at submit (resolvePlaceNewKind).
    case "place_new":
      fields.push({
        name: "place_tier", label: "Place as a new…", type: "select",
        options: [
          { value: "item", label: "Item (under a Category)" },
          { value: "action", label: "Action (under an Item)" },
          { value: "category", label: "Category" },
        ],
      });
      // The row's text/narrative prefill the name + narrative fields (#1).
      // (Re-applied on tier switch below — visibleWhen clears hidden fields.)
      const placePrefill = spec.bucketContext?.entryText;
      const placeNarrative = spec.bucketContext?.entryNarrative;
      // Category tier
      fields.push({
        name: "name", label: "Category name", type: "text", required: true,
        value: placePrefill,
        visibleWhen: { field: "place_tier", equals: ["category"] },
      });
      fields.push({
        name: "description", label: "Description (optional)", type: "textarea",
        value: placeNarrative,
        visibleWhen: { field: "place_tier", equals: ["category"] },
      });
      // Item tier (#1b deep-placement, 2026-06-19): cascade picker — pick the
      // parent Category, then optionally a Sub-category of it, so the new Item
      // is filed straight into the group in one triage step ("(Ungrouped)" =>
      // directly under the Category). Mirrors the #1a move cascade; the
      // Category <select> drives the Sub-category <select> (wired below). Falls
      // back to a typed Category text input when no destinations are wired.
      const placeCats = moveCascadeCategoryOptions(spec.threadOptions ?? []);
      if (placeCats.length) {
        fields.push({
          name: "new_item_thread_slug", label: "Under Category", type: "select",
          required: true, options: placeCats,
          visibleWhen: { field: "place_tier", equals: ["item"] },
        });
        fields.push({
          name: "new_item_subcategory_slug", label: "Sub-category",
          type: "select",
          options: placeSubcategoryOptions(
            spec.threadOptions ?? [], placeCats[0]!.value,
          ),
          visibleWhen: { field: "place_tier", equals: ["item"] },
        });
      } else {
        fields.push({
          name: "new_item_thread_slug", label: "Under Category", type: "text",
          required: true, visibleWhen: { field: "place_tier", equals: ["item"] },
        });
      }
      fields.push({
        name: "new_item_name", label: "Item name", type: "text",
        required: true, value: placePrefill,
        visibleWhen: { field: "place_tier", equals: ["item"] },
      });
      fields.push({
        name: "new_item_narrative", label: "Item narrative (2-3 lines, optional)",
        type: "textarea", value: placeNarrative,
        visibleWhen: { field: "place_tier", equals: ["item"] },
      });
      fields.push({
        name: "new_item_action_text", label: "First Action", type: "text",
        // Required for an OPEN Item (Decision 12.5); relaxes when 'complete'
        // (a closed Item can stand alone as a tombstone) — synced below.
        required: true, visibleWhen: { field: "place_tier", equals: ["item"] },
      });
      fields.push({
        name: "new_item_action_priority", label: "Action priority",
        type: "select", options: SETTABLE_PRIORITY_OPTIONS,
        visibleWhen: { field: "place_tier", equals: ["item"] },
      });
      // Action tier (parent Item picker wired below from itemOptions)
      fields.push({
        name: "target_work_item_slug", label: "Under Item", type: "text",
        required: true, visibleWhen: { field: "place_tier", equals: ["action"] },
      });
      fields.push({
        name: "text", label: "Action", type: "text", required: true,
        value: placePrefill,
        visibleWhen: { field: "place_tier", equals: ["action"] },
      });
      fields.push({
        name: "priority_status", label: "Priority", type: "select",
        options: SETTABLE_PRIORITY_OPTIONS,
        visibleWhen: { field: "place_tier", equals: ["action"] },
      });
      // The "file as closed" toggle (Case A).
      fields.push({
        name: "complete", type: "checkbox",
        label: "Already complete (file as closed)",
      });
      break;
    // "Merge Into Existing" — fold this row INTO an existing entity (Case B,
    // dedup; never creates active work). Pickers below offer OPEN and CLOSED
    // targets per tier; labels resolve to slugs at submit
    // (resolveMergeTargetPicks). text/source_ref/entry_id from bucketContext.
    case "merge_into_existing":
      fields.push({
        name: "target_tier", label: "Merge into an existing…", type: "select",
        options: [
          { value: "work_item", label: "Item" },
          { value: "action_item", label: "Action" },
          { value: "thread", label: "Category" },
        ],
      });
      // The Open/Closed toggle filters the picker below. Not a wire field —
      // stripped at submit; drives which targets the datalist offers.
      fields.push({
        name: "merge_scope", label: "Show", type: "select",
        options: [
          { value: "open", label: "Open" },
          { value: "closed", label: "Closed" },
        ],
      });
      fields.push({
        name: "target_work_item_slug", label: "Existing Item", type: "text",
        required: true, visibleWhen: { field: "target_tier", equals: ["work_item"] },
        note: "Open or closed. A see-also note is added under it (shown in Closed Items).",
      });
      fields.push({
        name: "target_action_pick", label: "Existing Action", type: "text",
        required: true, visibleWhen: { field: "target_tier", equals: ["action_item"] },
        note: "Recorded for the operator; no new entry appears in the portal.",
      });
      fields.push({
        name: "target_thread_slug", label: "Existing Category", type: "text",
        required: true, visibleWhen: { field: "target_tier", equals: ["thread"] },
        note: "Rarely the right pick — a Category is a container. Usually 'Place as New Item' is what you want.",
      });
      fields.push({
        name: "note", label: "Note (optional)", type: "textarea",
      });
      break;
    case "item_move_to_category": {
      // #1a UX (2026-06-19): cascade picker — pick a Category, then a second
      // <select> surfaces THAT Category's sub-categories (repopulated below).
      // Replaces the single flattened "Category › Sub-cat" list (functional but
      // undiscoverable when moving fast — operator feedback). to_thread_slug
      // stays the composite the submit reads (bare slug or slug/subslug) →
      // planDeepMoveDestination + the wire are unchanged. Falls back to a typed
      // Category text input when no destinations are wired (fixture).
      const moveCats = moveCascadeCategoryOptions(spec.threadOptions ?? []);
      if (moveCats.length) {
        fields.push({
          name: "to_category_pick", label: "Category", type: "select",
          required: true, options: moveCats,
        });
        fields.push({
          name: "to_thread_slug", label: "Sub-category", type: "select",
          required: true,
          options: moveCascadeSubcategoryOptions(
            spec.threadOptions ?? [], moveCats[0]!.value,
          ),
        });
      } else {
        fields.push({
          name: "to_thread_slug",
          label: "Move to Category", type: "text", required: true,
        });
      }
      // B10: optional "Move & Close" for a done-but-misfiled Item. Fires
      // work_item_close alongside the move (submit handler) so it lands in
      // the target Category's Recently Closed. Gated to closeable Items
      // (0 open Actions); disabled + B9 note when the Item has open work.
      fields.push({
        name: "also_close",
        label: "Also close this Item after moving (→ Recently Closed)",
        type: "checkbox",
        disabled: spec.itemCloseable === false,
        note: spec.itemCloseable === false
          ? "Close this Item's open Actions first, then it can be moved & closed."
          : undefined,
      });
      break;
    }

    // --- Task 3: work-item structural affordances ----------------------
    case "candidate_to_item":
      // Place this candidate suggestion as an Item under an existing
      // Category. target_thread_slug renders as a Category datalist
      // (wired below); work_item_slug is derived from name on submit.
      fields.push({
        name: "target_thread_slug",
        label: "Place under Category", type: "text", required: true,
      });
      fields.push({
        name: "name", label: "Item name", type: "text", required: true,
      });
      fields.push({
        name: "narrative",
        label: "Item narrative (2-3 lines, optional)", type: "textarea",
      });
      fields.push({
        name: "initial_action_text",
        label: "First action (optional — otherwise the Item starts with an informational note)",
        type: "textarea",
      });
      fields.push({
        name: "initial_action_priority",
        label: "First action priority",
        type: "select",
        options: [
          { value: "Active", label: "Active" },
          { value: "Critical", label: "Critical" },
          { value: "Delay Potential", label: "Delay Potential" },
          { value: "On Hold", label: "On Hold" },
        ],
      });
      break;
    case "work_item_rename":
      fields.push({
        name: "to_name", label: "New Item name", type: "text", required: true,
      });
      break;
    case "action_rename":
      fields.push({
        name: "to_text", label: "New Action text", type: "text", required: true,
      });
      break;
    case "work_item_merge": {
      // Multi-select of sibling Items to fold INTO this one (the menu is
      // open on the target). Options come from spec.itemOptions, target
      // excluded. The absorber skips any source == target defensively.
      const targetWi = (spec.targetId.split("work_item:")[1] ?? "").trim();
      const opts = (spec.itemOptions ?? [])
        .filter((i) => i.slug !== targetWi)
        .map((i) => ({
          value: i.slug,
          label: [i.display_number, i.name].filter(Boolean).join(" "),
        }));
      fields.push({
        name: "source_work_item_slugs",
        label: "Items to merge into this one (pick one or more)",
        type: "multiselect",
        required: true,
        options: opts,
      });
      fields.push({
        name: "target_name",
        label: "Rename the merged Item (optional)", type: "text",
      });
      break;
    }
    case "item_merge_into": {
      // #73 reverse merge: single-select of the EXISTING primary to fold THIS
      // Item up into (open Items project-wide, this one excluded). A <select>
      // (small fixed set; value IS the slug, no label→slug mapping). At submit
      // (resolveItemMergeInto) it becomes a work_item_merge whose target is the
      // picked primary and whose single source is this child.
      const selfWi = (spec.targetId.split("work_item:")[1] ?? "").trim();
      const opts = (spec.itemOptions ?? [])
        .filter((i) => i.slug !== selfWi)
        .map((i) => ({
          value: i.slug,
          label: [i.display_number, i.name, i.thread_name ? `· ${i.thread_name}` : ""]
            .filter(Boolean).join(" "),
        }));
      fields.push({
        name: "merge_primary_item",
        label: "Merge this Item into (pick the primary)",
        type: "select",
        required: true,
        options: opts,
        // N1 (2026-07-14): when the agent surfaced a "Possibly part of X" matter
        // hint on this Item, pre-select X (spec.current.merge_primary_item) so
        // the recipient just CONFIRMS the proposed merge — accept-the-proposal,
        // mirroring the closure_candidate affordance. A slug not in `opts` (the
        // target isn't open/rendered this week) leaves the first option — a safe
        // no-op — and the recipient picks manually as before.
        value: spec.current?.merge_primary_item ?? undefined,
      });
      // #66 D4: provenance, present ONLY when this form was opened by Accept
      // on an agent proposal. Hidden — the recipient is confirming a merge, not
      // filling this in — and absent entirely on a self-initiated merge, which
      // is what makes the tag a usable positive label.
      if (spec.current?.from_proposal) {
        fields.push({
          name: "from_proposal",
          label: "Accepted from proposal",
          type: "hidden",
          value: spec.current.from_proposal,
        });
      }
      break;
    }
    case "work_item_dismiss_merge_candidate": {
      // #66 D4: reject a proposed merge DURABLY. A hidden field, not a picker —
      // this form is only ever built programmatically by the proposal banner's
      // Dismiss button, which already knows the counterpart. Carrying the
      // counterpart is mandatory: the absorber rejects a half-pair, because a
      // dismissal recorded against one Item alone would suppress every future
      // proposal on it.
      fields.push({
        name: "target_work_item_slug",
        label: "Not a duplicate of",
        type: "hidden",
        required: true,
        value: spec.current?.target_work_item_slug ?? undefined,
      });
      break;
    }
    case "action_merge": {
      // B8: multi-select of open Actions to fold INTO this one — now
      // project-wide (any Item), not just siblings. Each option's value is
      // its full target id (so a cross-Item pick carries its origin Item);
      // the submit handler re-parents cross-Item picks before merging. The
      // target itself is excluded; a cross-Item option is labelled with its
      // parent Item so the recipient sees where it comes from.
      const targetWi = spec.targetId.match(/work_item:([^/]+)/)?.[1] ?? "";
      const opts = (spec.actionOptions ?? [])
        .filter((a) => (a.target_id ?? a.ai_slug) !== spec.targetId)
        .filter((a) => a.ai_slug !== ""
          && !(a.work_item_slug === targetWi && a.ai_slug === (
            spec.targetId.split("action_item:")[1] ?? "").trim()))
        .map((a) => {
          const base = [a.display_number, a.text].filter(Boolean).join(" ");
          const crossItem = !!a.work_item_slug && a.work_item_slug !== targetWi;
          return {
            value: a.target_id ?? a.ai_slug,
            label: crossItem && a.item_label
              ? `${base} · from ${a.item_label}`
              : base,
          };
        });
      fields.push({
        name: "source_action_item_slugs",
        label: "Actions to merge into this one (pick one or more)",
        type: "multiselect",
        required: true,
        options: opts,
      });
      fields.push({
        name: "target_text",
        label: "Rename the merged Action (optional)", type: "text",
      });
      break;
    }
    case "action_merge_into": {
      // B1 / #73 reverse merge: single-select of the EXISTING primary Action to
      // fold THIS Action up into (open Actions project-wide, this one excluded).
      // A <select> (value IS the full action_item target id, so the picked
      // primary's Item is known at submit for the POST target + cross-Item
      // re-parent). At submit (resolveActionMergeInto) it becomes an action_merge
      // whose target is the picked primary and whose single source is this child.
      const opts = (spec.actionOptions ?? [])
        .filter((a) => (a.target_id ?? a.ai_slug) !== spec.targetId
          && a.ai_slug !== "")
        .map((a) => {
          const base = [a.display_number, a.text].filter(Boolean).join(" ");
          return {
            value: a.target_id ?? a.ai_slug,
            label: a.item_label ? `${base} · in ${a.item_label}` : base,
          };
        });
      fields.push({
        name: "merge_primary_action",
        label: "Merge this Action into (pick the primary)",
        type: "select",
        required: true,
        options: opts,
      });
      break;
    }
    case "action_move_to_item":
      // B8: re-parent this Action to a different Item. The destination is
      // picked from a project-wide Item datalist (spec.itemOptions); the
      // value submitted is the Item slug.
      fields.push({
        name: "to_work_item_slug",
        label: "Move to Item", type: "text", required: true,
      });
      break;
    case "action_to_item": {
      // #76: promote this Action to a NEW Item. Name prefills with the Action
      // text (PREFILL_KINDS / spec.current.name) — edit or keep it; reshape
      // derives new_work_item_slug from it. Destination is a <select> of open
      // Categories (value IS the slug — no label→slug mapping).
      fields.push({
        name: "name",
        label: "Name for the new Item", type: "text", required: true,
      });
      const promoteCats = (spec.threadOptions ?? []).filter((t) => !t.closed);
      fields.push({
        name: "to_thread_slug",
        label: "Under Category", type: "select", required: true,
        options: promoteCats.map((t) => ({ value: t.slug, label: threadOptionLabel(t) })),
      });
      // `a` (Ray 2026-07-22): sub-category cascade — pick the destination Category,
      // then optionally a Sub-category of it, so the promoted Item files straight
      // into the group ("(Ungrouped)" => directly under the Category). Mirrors the
      // place cascade (#1b); the to_thread_slug <select> drives this one (wired
      // below). Bare-slug values; the absorber reads new_item_subcategory_slug.
      if (promoteCats.length) {
        fields.push({
          name: "new_item_subcategory_slug", label: "Sub-category", type: "select",
          options: placeSubcategoryOptions(spec.threadOptions ?? [], promoteCats[0]!.slug),
        });
      }
      break;
    }

    // --- #38 sub-category grouping -------------------------------------
    case "subcategory_add":
      // Mints an unnumbered grouping under this Category. The slug is
      // collision-minted at submit (mintSubcategorySlug); only the display
      // name is typed here.
      fields.push({
        name: "name", label: "Sub-category name", type: "text", required: true,
      });
      break;
    case "subcategory_rename":
      // Pre-filled with the current name (spec.current.to_name, PREFILL_KINDS).
      fields.push({
        name: "to_name", label: "New sub-category name", type: "text",
        required: true,
      });
      break;
    case "subcategory_close":
      fields.push({
        name: "reason",
        label: "Reason (optional — e.g. 'work complete')", type: "textarea",
      });
      break;
    case "subcategory_delete":
      // #74b: no close-gate — delete ungroups a non-empty group on purpose.
      // The helper text explains members fall back to ungrouped (nothing
      // closed). Optional reason for the audit log, like close.
      fields.push({
        name: "reason",
        label: "Reason (optional — e.g. 'grouped these by mistake')",
        type: "textarea",
      });
      break;
    case "subcategory_reopen":
      fields.push({
        name: "reason", label: "Reason (optional)", type: "textarea",
      });
      break;
    case "item_move_to_subcategory": {
      // Closed-set picker: this Category's OPEN groups + an explicit
      // "(ungrouped)". A <select> (not a datalist) since the option set is
      // small and fixed — the value IS the slug, no label→slug mapping. Not
      // `required`: every option (incl. "(ungrouped)" = "") is a valid pick.
      // Pre-selects the Item's current group via f.value (spec.current).
      const openGroups = (spec.subcategoryOptions ?? []).filter((s) => !s.closed);
      fields.push({
        name: "to_subcategory_slug",
        label: "Move to sub-category",
        type: "select",
        options: [
          ...openGroups.map((s) => ({ value: s.slug, label: s.name })),
          { value: "", label: "(ungrouped)" },
        ],
        value: spec.current?.to_subcategory_slug ?? "",
      });
      break;
    }
    case "category_move_to_phase": {
      // #37: re-band this Category. Closed 4-value enum <select> (no label→
      // value mapping — the value IS the wire `to_phase` enum). Pre-selects
      // the Category's current phase; a null/legacy phase defaults to General.
      fields.push({
        name: "to_phase",
        label: "Move to phase",
        type: "select",
        required: true,
        options: [
          { value: "pre_development", label: "Pre-Development" },
          { value: "pre_construction", label: "Pre-Construction" },
          { value: "construction", label: "Construction" },
          { value: "general", label: "General" },
        ],
        value: spec.current?.phase ?? "general",
      });
      break;
    }
    case "item_move": {
      // Feedback 2.1: ONE Move form when both destinations exist. The tier
      // select reveals the matching picker (visibleWhen, the Decision-3C
      // reveal); resolveItemMoveKind maps to the real wire kind at submit —
      // the place_new pseudo-kind pattern. Category tier reuses the
      // item_move_to_category fields verbatim (datalist + B10 Move & Close);
      // Sub-category tier reuses the item_move_to_subcategory <select>.
      const openGroups = (spec.subcategoryOptions ?? []).filter((s) => !s.closed);
      fields.push({
        name: "move_tier",
        label: "Move to",
        type: "select",
        required: true,
        options: [
          { value: "category", label: "Another Category" },
          { value: "subcategory", label: "A sub-category of this Category" },
        ],
      });
      // #1a UX (2026-06-19): the Category tier's destination is the same
      // cascade as item_move_to_category — pick a Category, then its
      // sub-categories (repopulated below). Gated to the "category" move_tier.
      const pseudoMoveCats = moveCascadeCategoryOptions(spec.threadOptions ?? []);
      if (pseudoMoveCats.length) {
        fields.push({
          name: "to_category_pick", label: "Category", type: "select",
          required: true, options: pseudoMoveCats,
          visibleWhen: { field: "move_tier", equals: ["category"] },
        });
        fields.push({
          name: "to_thread_slug", label: "Sub-category", type: "select",
          required: true,
          options: moveCascadeSubcategoryOptions(
            spec.threadOptions ?? [], pseudoMoveCats[0]!.value,
          ),
          visibleWhen: { field: "move_tier", equals: ["category"] },
        });
      } else {
        fields.push({
          name: "to_thread_slug",
          label: "Destination Category", type: "text", required: true,
          visibleWhen: { field: "move_tier", equals: ["category"] },
        });
      }
      fields.push({
        name: "also_close",
        label: "Also close this Item after moving (→ Recently Closed)",
        type: "checkbox",
        disabled: spec.itemCloseable === false,
        note: spec.itemCloseable === false
          ? "Close this Item's open Actions first, then it can be moved & closed."
          : undefined,
        visibleWhen: { field: "move_tier", equals: ["category"] },
      });
      fields.push({
        name: "to_subcategory_slug",
        label: "Destination sub-category",
        type: "select",
        options: [
          ...openGroups.map((s) => ({ value: s.slug, label: s.name })),
          { value: "", label: "(ungrouped)" },
        ],
        value: spec.current?.to_subcategory_slug ?? "",
        visibleWhen: { field: "move_tier", equals: ["subcategory"] },
      });
      break;
    }

    // --- v4 Billing ----------------------------------------------------
    // Decision 8: status enums become <select>; clients no longer
    // type the literal. Decision 7A: invoice_number on the two
    // status-mutation forms becomes a datalist of invoices on this
    // Item, populated below from spec.invoiceOptions.
    case "invoice_add":
      fields.push({
        name: "number", label: "Invoice number", type: "text", required: true,
      });
      fields.push({
        name: "amount", label: "Amount (numeric)", type: "text",
      });
      fields.push({
        name: "status",
        label: "Status",
        type: "select",
        options: [
          { value: "Received", label: "Received" },
          { value: "Under Review", label: "Under Review" },
          { value: "Approved", label: "Approved" },
          { value: "Paid", label: "Paid" },
          { value: "Disputed", label: "Disputed" },
        ],
      });
      fields.push({
        name: "received_date", label: "Received date", type: "date",
      });
      fields.push({
        name: "due_date", label: "Due date", type: "date",
      });
      fields.push({
        name: "blocking_note", label: "Blocking note (optional)", type: "textarea",
      });
      break;
    case "invoice_status_change":
      fields.push({
        name: "invoice_number", label: "Invoice", type: "text", required: true,
      });
      fields.push({
        name: "new_status",
        label: "New status",
        type: "select",
        required: true,
        options: [
          { value: "Received", label: "Received" },
          { value: "Under Review", label: "Under Review" },
          { value: "Approved", label: "Approved" },
          { value: "Paid", label: "Paid" },
          { value: "Disputed", label: "Disputed" },
        ],
      });
      break;
    case "invoice_blocking_note_set":
      fields.push({
        name: "invoice_number", label: "Invoice", type: "text", required: true,
      });
      fields.push({
        name: "blocking_note",
        label: "Blocking note (leave blank to clear)",
        type: "textarea",
      });
      break;
  }

  // Pre-fill edit forms with the target's current value (spec.current) so the
  // recipient edits what's already there instead of starting from blank. Gated
  // to the explicit edit kinds — "add new" forms (action_add,
  // action_open_question_add) share field names like "text" but must stay empty.
  const PREFILL_KINDS = new Set([
    "action_set_reason", "action_set_impact",
    "action_set_off_hold_condition", "action_set_due",
    "action_priority_change",
    // Rename forms: pre-fill the current Category / Item name to edit
    // (thread_rename's field is "new_name", work_item_rename's is "to_name").
    "thread_rename", "work_item_rename",
    // B6: action_rename pre-fills the current Action text ("to_text").
    "action_rename",
    // #76: action_to_item pre-fills the new Item's name ("name") with the
    // Action text (spec.current.name) — edit or keep it.
    "action_to_item",
    // A³ (2026-06-03): thread_promote pre-fills the candidate's suggested
    // name ("name") so the recipient edits it inline while promoting.
    "thread_promote",
    // #38: subcategory_rename pre-fills the current group name ("to_name").
    "subcategory_rename",
    // R2-3: pre-fill the current narrative ("narrative") so it's an EDIT.
    "work_item_set_narrative", "action_set_narrative",
    // R2-3 fast-follow: pre-fill the current exec summary ("summary").
    "set_executive_summary",
    // Round-4 #2: the flattened details form pre-fills every shown field
    // (reason / impact / off_hold_condition / due) — change-detection in
    // collectActionDetailEdits compares against these same current values.
    "action_edit_details",
  ]);
  if (spec.current && PREFILL_KINDS.has(kind)) {
    for (const f of fields) {
      if (f.value === undefined
          && (f.type === "text" || f.type === "textarea" || f.type === "date")) {
        const cur = spec.current[f.name];
        if (cur != null && cur !== "") f.value = cur;
      }
    }
  }

  // Track field rows keyed by name so visibleWhen logic can show/hide
  // them after the form is built (Decision 3C). Each row is a single
  // <div.form-row>; toggling display:none is sufficient.
  const fieldRows = new Map<string, HTMLDivElement>();
  for (const f of fields) {
    const row = document.createElement("div");
    row.className = "form-row";
    const label = document.createElement("label");
    label.textContent = f.label;
    let input: HTMLElement;
    if (f.type === "textarea") {
      input = document.createElement("textarea");
      (input as HTMLTextAreaElement).name = f.name;
      (input as HTMLTextAreaElement).rows = 3;
      if (f.required) (input as HTMLTextAreaElement).required = true;
      if (f.value !== undefined) (input as HTMLTextAreaElement).value = f.value;
    } else if (f.type === "select") {
      const sel = document.createElement("select");
      sel.name = f.name;
      if (f.required) sel.required = true;
      for (const o of f.options ?? []) {
        const opt = document.createElement("option");
        opt.value = o.value;
        opt.textContent = o.label;
        sel.appendChild(opt);
      }
      // #38: pre-select an initial value when given (e.g. the Item's current
      // sub-category on the move form). A value matching no option leaves the
      // browser on the first option — no-op for the existing selects that
      // never set f.value.
      if (f.value !== undefined) sel.value = f.value;
      input = sel;
    } else if (f.type === "multiselect") {
      // Checkbox list, NOT a native <select multiple>: long option labels
      // (e.g. "3.2.5 Advise security and assist Orlim with locked-area access")
      // overflow / clip unreliably inside the 320px popover across browsers.
      // Checkboxes let the labels wrap and stay contained. Each shares the
      // field name, so fd.getAll(name) collects the checked values exactly as
      // the old <select multiple> did — the submit path is unchanged.
      const list = document.createElement("div");
      list.className = "multiselect-list";
      for (const o of f.options ?? []) {
        const opt = document.createElement("label");
        opt.className = "multiselect-option";
        const cb = document.createElement("input");
        cb.type = "checkbox";
        cb.name = f.name;
        cb.value = o.value;
        const span = document.createElement("span");
        span.textContent = o.label;
        opt.appendChild(cb);
        opt.appendChild(span);
        list.appendChild(opt);
      }
      input = list;
    } else if (f.type === "hidden") {
      // No label, no row chrome — append the bare input and move on, so a
      // programmatic form (the merge-proposal Dismiss) renders as nothing.
      const hid = document.createElement("input");
      hid.type = "hidden";
      hid.name = f.name;
      hid.value = f.value ?? "";
      form.appendChild(hid);
      continue;
    } else if (f.type === "checkbox") {
      // Checkbox renders inline with its label text to the right —
      // FormData only includes the entry when checked, so the reshape
      // step coerces presence → true, absence → false.
      input = document.createElement("input");
      (input as HTMLInputElement).name = f.name;
      (input as HTMLInputElement).type = "checkbox";
      if (f.disabled) (input as HTMLInputElement).disabled = true;
      const labelText = document.createElement("span");
      labelText.textContent = f.label;
      label.textContent = "";
      label.classList.add("form-checkbox-label");
      label.appendChild(input);
      label.appendChild(labelText);
      row.appendChild(label);
      if (f.note) {
        const note = document.createElement("p");
        note.className = "form-helper";
        note.textContent = f.note;
        row.appendChild(note);
      }
      form.appendChild(row);
      fieldRows.set(f.name, row);
      continue;
    } else {
      input = document.createElement("input");
      (input as HTMLInputElement).name = f.name;
      (input as HTMLInputElement).type = f.type === "date" ? "date" : "text";
      if (f.required) (input as HTMLInputElement).required = true;
      if (f.value !== undefined) (input as HTMLInputElement).value = f.value;
    }
    label.appendChild(input);
    row.appendChild(label);
    form.appendChild(row);
    fieldRows.set(f.name, row);
  }

  // Decision 3C: conditional reveal. A field marked `visibleWhen`
  // listens to changes on its driver field and shows/hides its row.
  // Hidden rows clear their input value on hide so submission doesn't
  // carry stale data from a different priority.
  const conditionals = fields.filter((f) => f.visibleWhen);
  for (const f of conditionals) {
    const row = fieldRows.get(f.name);
    const driver = form.querySelector<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >(`[name="${f.visibleWhen!.field}"]`);
    if (!row || !driver) continue;
    const apply = (): void => {
      const equals = f.visibleWhen!.equals;
      const matches = equals.includes(driver.value);
      row.style.display = matches ? "" : "none";
      const el = row.querySelector<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >(`[name="${f.name}"]`);
      if (el) {
        // Hidden `required` inputs silently block submit via HTML5
        // validation (the browser tries to focus an invisible field
        // and the user sees nothing). Sync `required` with visibility
        // so the only constraints enforced are the ones the user can
        // actually see.
        if (matches && f.required) {
          el.setAttribute("required", "");
        } else {
          el.removeAttribute("required");
        }
        if (!matches && el.tagName !== "SELECT") el.value = "";
      }
    };
    driver.addEventListener("change", apply);
    driver.addEventListener("input", apply);
    apply();
  }

  // #1a/#1b: cascade the Category → Sub-category pickers — when the Category
  // <select> changes, repopulate the dependent Sub-category <select>. Used by
  // the move forms (to_category_pick → to_thread_slug, composite values) and the
  // place form (new_item_thread_slug → new_item_subcategory_slug, bare slugs).
  // Each is a no-op when its driver/dependent pair isn't present in this form.
  wireSubcategoryCascade(
    form, "to_category_pick", "to_thread_slug",
    (cat) => moveCascadeSubcategoryOptions(spec.threadOptions ?? [], cat),
  );
  wireSubcategoryCascade(
    form, "new_item_thread_slug", "new_item_subcategory_slug",
    (cat) => placeSubcategoryOptions(spec.threadOptions ?? [], cat),
  );
  // `a` (Ray 2026-07-22): the action→item promote form reuses new_item_subcategory_slug
  // but its Category driver is to_thread_slug (no-op on any form lacking that pair).
  wireSubcategoryCascade(
    form, "to_thread_slug", "new_item_subcategory_slug",
    (cat) => placeSubcategoryOptions(spec.threadOptions ?? [], cat),
  );

  // #60 PR4-v2: on Place-as-New → Item, the first Action is required for an
  // OPEN Item (Decision 12.5) but optional when "Already complete" is ticked
  // (a closed Item can stand alone as a tombstone). Sync `required` off the
  // checkbox, layered on top of the visibleWhen required-sync above.
  if (kind === "place_new") {
    const complete = form.querySelector<HTMLInputElement>('input[name="complete"]');
    const tier = form.querySelector<HTMLSelectElement>('select[name="place_tier"]');
    const firstAction = form.querySelector<HTMLInputElement>(
      'input[name="new_item_action_text"]',
    );
    if (complete && tier && firstAction) {
      const syncFirstAction = (): void => {
        const itemTier = tier.value === "item";
        firstAction.toggleAttribute("required", itemTier && !complete.checked);
      };
      complete.addEventListener("change", syncFirstAction);
      tier.addEventListener("change", syncFirstAction);
      syncFirstAction();
    }
    // #1: re-apply the row's prefill to the active tier's fields on switch —
    // visibleWhen clears hidden fields, so each tier's name/narrative must be
    // re-seeded when it becomes visible (registered AFTER visibleWhen's apply,
    // so it runs second on the same change event). Fill-if-empty: don't
    // clobber a value the user already typed.
    const prefillText = spec.bucketContext?.entryText ?? "";
    const prefillNarr = spec.bucketContext?.entryNarrative ?? "";
    const prefillMap: Record<string, string> = {
      name: prefillText, new_item_name: prefillText, text: prefillText,
      new_item_narrative: prefillNarr, description: prefillNarr,
    };
    if (tier && (prefillText || prefillNarr)) {
      const reapplyPrefill = (): void => {
        for (const [fieldName, val] of Object.entries(prefillMap)) {
          if (!val) continue;
          const el = form.querySelector<HTMLInputElement | HTMLTextAreaElement>(
            `[name="${fieldName}"]`,
          );
          // Only the active-tier (visible) field, and only if empty.
          if (el && el.offsetParent !== null && !el.value) el.value = val;
        }
      };
      tier.addEventListener("change", reapplyPrefill);
    }
  }

  // Slice 8 Item #5: responsible_party_assign autocomplete via
  // <datalist>. Sourced from the briefing's participant-options
  // collector (see briefings.ts). Selecting an option that has a
  // known email auto-fills the email field. Free-text "Add new
  // person…" path is implicit — typing a name not in the list still
  // submits cleanly.
  if (kind === "responsible_party_assign" && spec.participantOptions?.length) {
    const listId = `participants-${spec.targetId.replace(/[^a-z0-9-]/gi, "_")}`;
    const datalist = document.createElement("datalist");
    datalist.id = listId;
    const emailByName = new Map<string, string>();
    for (const p of spec.participantOptions) {
      const opt = document.createElement("option");
      opt.value = p.name;
      datalist.appendChild(opt);
      if (p.email) emailByName.set(p.name, p.email);
    }
    form.appendChild(datalist);
    const nameInput = form.querySelector<HTMLInputElement>('input[name="name"]');
    const emailInput = form.querySelector<HTMLInputElement>('input[name="email"]');
    if (nameInput) {
      nameInput.setAttribute("list", listId);
      nameInput.setAttribute(
        "placeholder",
        "Pick from list or type a new name",
      );
      // Auto-fill email when the user picks a participant whose email
      // we know. Only writes when the email field is currently empty
      // so we don't clobber a manually-typed value.
      nameInput.addEventListener("input", () => {
        if (!emailInput || emailInput.value.trim()) return;
        const matched = emailByName.get(nameInput.value.trim());
        if (matched) emailInput.value = matched;
      });
    }
  }

  // Thread datalist for thread_merge. thread_retag was dropped from
  // the SPA in Decision 2 close-out (operator-only via CLI); the
  // datalist wiring shrinks to thread_merge alone.
  const threadKinds = new Set(["thread_merge"]);
  if (threadKinds.has(kind) && spec.threadOptions?.length) {
    const listId = `threads-${spec.targetId.replace(/[^a-z0-9-]/gi, "_")}-${kind}`;
    const datalist = document.createElement("datalist");
    datalist.id = listId;
    for (const t of spec.threadOptions) {
      const opt = document.createElement("option");
      // #63: value is what fills the input on selection AND what most
      // browsers show in the dropdown — so use the human label "N name",
      // never the slug (a technical artifact the client doesn't need). The
      // picked label is mapped back to the stable slug at submit via
      // resolvePickedTargetSlugs (NOT slugify — slug survives a rename).
      const lbl = threadOptionLabel(t);
      opt.value = lbl;
      opt.label = lbl;
      opt.textContent = lbl;
      datalist.appendChild(opt);
    }
    form.appendChild(datalist);
    const input = form.querySelector<HTMLInputElement>(
      'input[name="merge_into"]',
    );
    if (input) {
      input.setAttribute("list", listId);
      input.setAttribute("placeholder", "Pick a Category");
    }
  }

  // Decision 7A (close-out 2026-05-25): Category datalist for candidate_to_item.
  // #63: value=NAME (not slug) — mapped back to slug at submit.
  // #1a/#1b (2026-06-19): item_move_to_category, item_move, and place_new's
  // Item tier are now Category <select>s (the cascade), so they no longer use
  // this typed datalist — only candidate_to_item still does.
  const categoryPickerKinds = new Set(["candidate_to_item"]);
  if (categoryPickerKinds.has(kind) && spec.threadOptions?.length) {
    const listId =
      `cats-${spec.targetId.replace(/[^a-z0-9-]/gi, "_")}-${kind}`;
    const datalist = document.createElement("datalist");
    datalist.id = listId;
    for (const t of spec.threadOptions) {
      const opt = document.createElement("option");
      const lbl = threadOptionLabel(t);
      opt.value = lbl;
      opt.label = lbl;
      opt.textContent = lbl;
      datalist.appendChild(opt);
    }
    form.appendChild(datalist);
    const inputName = "target_thread_slug";
    const input = form.querySelector<HTMLInputElement>(
      `input[name="${inputName}"]`,
    );
    if (input) {
      input.setAttribute("list", listId);
      input.setAttribute("placeholder", "Pick a Category");
    }
  }

  // Item datalist for the Place-as-Action tier (A³/A², 2026-06-03 — was
  // place_from_unfiled's place-under-existing path). Value=slug,
  // label="N.M Item name".
  // B8: action_move_to_item uses the same project-wide Item datalist on a
  // `to_work_item_slug` field; place_as_action uses `target_work_item_slug`.
  // Item datalist (OPEN items). place_as_action / action_move_to_item, and
  // PR4-v2 place_new's Action tier (target_work_item_slug = parent Item).
  if (
    (kind === "place_as_action" || kind === "action_move_to_item"
      || kind === "place_new")
    && spec.itemOptions?.length
  ) {
    const listId = `items-${spec.targetId.replace(/[^a-z0-9-]/gi, "_")}-${kind}`;
    const datalist = document.createElement("datalist");
    datalist.id = listId;
    // For a move, exclude the Action's current parent Item from the picker.
    const currentWi = ((spec.targetId.split("work_item:")[1] ?? "")
      .split("/")[0] ?? "").trim();
    for (const i of spec.itemOptions) {
      if (kind === "action_move_to_item" && i.slug === currentWi) continue;
      const opt = document.createElement("option");
      // #63: value=friendly label ("N.M name (Category)"), not the slug;
      // resolvePickedTargetSlugs maps it back via the same itemOptionLabel.
      const lbl = itemOptionLabel(i);
      opt.value = lbl;
      opt.label = lbl;
      opt.textContent = lbl;
      datalist.appendChild(opt);
    }
    form.appendChild(datalist);
    const fieldName = kind === "action_move_to_item"
      ? "to_work_item_slug" : "target_work_item_slug";
    const input = form.querySelector<HTMLInputElement>(
      `input[name="${fieldName}"]`,
    );
    if (input) {
      input.setAttribute("list", listId);
      input.setAttribute("placeholder", "Pick an Item");
    }
  }

  // #60 PR4-v2 Merge Into Existing: one datalist per tier, REPOPULATED by the
  // Open/Closed `merge_scope` toggle. Targets are OPEN+CLOSED (merge*Options),
  // each carrying `closed`; the toggle filters to one. Separate source from
  // the open-only Place pickers. #63 labels; resolveMergeTargetPicks maps back.
  if (kind === "merge_into_existing") {
    const idSafe = spec.targetId.replace(/[^a-z0-9-]/gi, "_");
    const scopeSel = form.querySelector<HTMLSelectElement>(
      'select[name="merge_scope"]',
    );
    // (fieldName, listId, all options w/ closed flag, label fn, placeholder)
    const pickers: Array<{
      field: string; listId: string;
      opts: Array<{ value: string; closed?: boolean }>; placeholder: string;
    }> = [
      {
        field: "target_work_item_slug", listId: `merge-items-${idSafe}`,
        opts: (spec.mergeItemOptions ?? []).map((i) => ({ value: itemOptionLabel(i), closed: i.closed })),
        placeholder: "Pick an existing Item",
      },
      {
        field: "target_thread_slug", listId: `merge-cats-${idSafe}`,
        opts: (spec.mergeThreadOptions ?? []).map((t) => ({ value: threadOptionLabel(t), closed: t.closed })),
        placeholder: "Pick an existing Category",
      },
      {
        field: "target_action_pick", listId: `merge-actions-${idSafe}`,
        opts: (spec.mergeActionOptions ?? []).map((a) => ({ value: a.label, closed: a.closed })),
        placeholder: "Pick an existing Action",
      },
    ];
    for (const p of pickers) {
      const datalist = document.createElement("datalist");
      datalist.id = p.listId;
      form.appendChild(datalist);
      const input = form.querySelector<HTMLInputElement>(`input[name="${p.field}"]`);
      if (input) {
        input.setAttribute("list", p.listId);
        input.setAttribute("placeholder", p.placeholder);
      }
    }
    const repopulate = (): void => {
      const wantClosed = scopeSel?.value === "closed";
      for (const p of pickers) {
        const datalist = form.querySelector<HTMLDataListElement>(`#${CSS.escape(p.listId)}`);
        const input = form.querySelector<HTMLInputElement>(`input[name="${p.field}"]`);
        if (!datalist) continue;
        datalist.replaceChildren();
        for (const o of p.opts) {
          if (!!o.closed !== wantClosed) continue;
          const opt = document.createElement("option");
          opt.value = o.value;
          opt.label = o.value;
          opt.textContent = o.value;
          datalist.appendChild(opt);
        }
        // Scope change invalidates a prior pick from the other scope.
        if (input && input.value) input.value = "";
      }
    };
    scopeSel?.addEventListener("change", repopulate);
    repopulate();
  }

  // Decision 7A: Invoice datalist for invoice_status_change and
  // invoice_blocking_note_set. Value=invoice number string;
  // label combines number + amount + status for disambiguation.
  const invoicePickerKinds = new Set([
    "invoice_status_change", "invoice_blocking_note_set",
  ]);
  if (invoicePickerKinds.has(kind) && spec.invoiceOptions?.length) {
    const listId =
      `inv-${spec.targetId.replace(/[^a-z0-9-]/gi, "_")}-${kind}`;
    const datalist = document.createElement("datalist");
    datalist.id = listId;
    for (const inv of spec.invoiceOptions) {
      const opt = document.createElement("option");
      opt.value = inv.number;
      opt.label = inv.label;
      opt.textContent = inv.label;
      datalist.appendChild(opt);
    }
    form.appendChild(datalist);
    const input = form.querySelector<HTMLInputElement>(
      'input[name="invoice_number"]',
    );
    if (input) {
      input.setAttribute("list", listId);
      input.setAttribute("placeholder", "Pick an invoice");
    }
  }

  const actions = document.createElement("div");
  actions.className = "form-actions";

  const submit = document.createElement("button");
  submit.type = "submit";
  submit.className = "btn-submit";
  // Cascade-close: the button carries the composite's scope ("Close Item +
  // 2 Actions") so the click is unambiguous about what it fires.
  const cascadeCount = kind === "work_item_close_cascade"
    ? (spec.cascadeOpenActions ?? []).length
    : 0;
  submit.textContent = cascadeCount
    ? `Close Item + ${cascadeCount} Action${cascadeCount === 1 ? "" : "s"}`
    : "Submit";
  actions.appendChild(submit);
  // Cascade-close: children closed by THIS form instance — survives a retry
  // after a partial failure so succeeded closes are never re-POSTed.
  const cascadeClosedChildren = new Set<string>();

  const back = document.createElement("button");
  back.type = "button";
  back.className = "btn-link";
  back.textContent = "← Back";
  back.addEventListener("click", onBack);
  actions.appendChild(back);

  form.appendChild(actions);

  const status = document.createElement("p");
  status.className = "form-status";
  form.appendChild(status);

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    submit.disabled = true;
    status.textContent = "Submitting…";
    const raw: Record<string, unknown> = {};
    const fd = new FormData(form);
    for (const [k, v] of fd.entries()) raw[k] = v;
    // <select multiple> emits one FormData entry per selection; the loop
    // above keeps only the last. Re-collect multiselects as arrays.
    form.querySelectorAll<HTMLSelectElement>("select[multiple]").forEach((sel) => {
      raw[sel.name] = fd.getAll(sel.name);
    });
    // Checkbox-list multiselects (.multiselect-list — the v0.4.19 replacement
    // for <select multiple>) ALSO emit one FormData entry per checked box
    // sharing a name; the entries-loop above keeps only the last, so a single
    // selection arrived as a bare string and the absorber's list contract
    // dropped the whole merge event silently. Re-collect as arrays too (deduped
    // by name) — this is the getAll the render site assumed but the submit
    // path never did.
    const seenMultiselect = new Set<string>();
    form
      .querySelectorAll<HTMLInputElement>(".multiselect-list input[type=checkbox]")
      .forEach((cb) => {
        if (seenMultiselect.has(cb.name)) return;
        seenMultiselect.add(cb.name);
        raw[cb.name] = fd.getAll(cb.name);
      });
    // Round-4 #2: action_edit_details decomposes into one wire event per
    // CHANGED field. All but the LAST are POSTed here, each bubbling its own
    // feedback-submitted (the action_merge multi-event pattern); the last
    // rides the generic path below as `wireKind`, so the success / chip /
    // status flow is unchanged. Nothing changed → friendly status, no POST.
    let detailEditLast:
      | { kind: FeedbackKind; payload: Record<string, unknown> }
      | null = null;
    if (kind === "action_edit_details") {
      const edits = collectActionDetailEdits(raw, spec.current);
      if (!edits.length) {
        status.textContent = "No changes to submit.";
        submit.disabled = false;
        return;
      }
      for (const e of edits.slice(0, -1)) {
        const res: { event_id: string } = inFixtureMode()
          ? { event_id: crypto.randomUUID() }
          : await postJson<{ event_id: string }>(
              `/feedback/${encodeURIComponent(spec.runId)}`,
              {
                kind: e.kind,
                target: { type: spec.targetType, id: spec.targetId },
                payload: e.payload,
              },
            );
        document.dispatchEvent(new CustomEvent("feedback-submitted", {
          detail: {
            event_id: res.event_id, kind: e.kind, payload: e.payload,
            submitted_at: new Date().toISOString(),
            targetId: spec.targetId, targetType: spec.targetType,
          },
        }));
      }
      const last = edits[edits.length - 1]!;
      detailEditLast = last;
      // The generic path reshapes `raw` for wireKind — swap in the last
      // edit's clean single-field payload so stray sibling fields (an
      // unchanged prefilled reason, the empty due) never reach the wire.
      for (const k of Object.keys(raw)) delete raw[k];
      Object.assign(raw, last.payload);
    }
    // Cascade-close: sequentially close the open children FIRST (one EXISTING
    // action_close per child, stamped "Closed with Item <N>"), then fall
    // through to the generic path as a plain work_item_close whose #74a
    // client_events carry the just-POSTed child closes past the Worker's
    // open-children gate (deterministic — the gate merges client_events, no
    // KV list-lag dependence; the proven B10 contingent-close pattern).
    // Mid-loop dispatches feedback-submitted ONLY: a feedback-state-changed
    // re-render here would tear down this popover with the loop in flight.
    // All state-changed echoes ride the post-success block below. Own
    // try/catch (the B8/action_edit_details precedents leave pre-loop
    // failures as unhandled rejections stuck on "Submitting…"): a mid-loop
    // failure reports partial progress and re-enables Submit — the retry
    // skips the already-closed children (each is individually retractable).
    if (kind === "work_item_close_cascade") {
      const childReason = cascadeChildReason(spec.movingFromDisplayNumber);
      const pending = pendingCascadeChildren(
        spec.cascadeOpenActions, cascadeClosedChildren,
      );
      try {
        for (const child of pending) {
          const childPayload: Record<string, unknown> = { reason: childReason };
          const res: { event_id: string } = inFixtureMode()
            ? { event_id: crypto.randomUUID() }
            : await postJson<{ event_id: string }>(
                `/feedback/${encodeURIComponent(spec.runId)}`,
                {
                  kind: "action_close",
                  target: { type: "action_item", id: child.target_id },
                  payload: childPayload,
                },
              );
          cascadeClosedChildren.add(child.target_id);
          document.dispatchEvent(new CustomEvent("feedback-submitted", {
            detail: {
              event_id: res.event_id, kind: "action_close",
              payload: childPayload, submitted_at: new Date().toISOString(),
              targetId: child.target_id, targetType: "action_item",
            },
          }));
        }
      } catch {
        const done = cascadeClosedChildren.size;
        const total = (spec.cascadeOpenActions ?? []).length;
        status.textContent =
          `Closed ${done} of ${total} Actions — the Item is still open. `
          + "Submit again to retry the rest.";
        submit.disabled = false;
        return;
      }
    }
    // #60 PR4-v2 / feedback 2.1: resolve the pseudo-kinds (place_new,
    // item_move) to their real wire kind by their tier selects. Everything
    // downstream (reshape, payload injection, the POSTed kind, optimistic
    // decoration) uses `wireKind`, never the pseudo.
    let wireKind: FeedbackKind = detailEditLast
      ? detailEditLast.kind
      : kind === "place_new"
        ? resolvePlaceNewKind(raw)
        : kind === "item_move"
          ? resolveItemMoveKind(raw)
          : kind === "item_merge_into"
            ? "work_item_merge"
            : kind === "action_merge_into"
              ? "action_merge"
              // Cascade-close: the children were closed in the pre-loop above;
              // the Item itself rides the wire as a plain work_item_close.
              : kind === "work_item_close_cascade"
                ? "work_item_close"
                : kind;
    // #73 reverse merge: the pseudo lives on the CHILD; fold it UP into the
    // picked primary. Override the POST target to that primary (work_item_merge
    // folds the source — this child — INTO the target). Capture the primary's
    // label BEFORE resolveItemMergeInto drops the picker field, for the
    // optimistic "merged into X" closed_reason on the child.
    let effectiveTargetId = spec.targetId;
    let reverseMergePrimaryLabel = "";
    if (kind === "item_merge_into") {
      const primarySlug = String(raw.merge_primary_item ?? "").trim();
      const opt = (spec.itemOptions ?? []).find((i) => i.slug === primarySlug);
      if (opt) {
        reverseMergePrimaryLabel = [opt.display_number, opt.name]
          .filter(Boolean).join(" ");
      }
      const primaryTargetId = resolveItemMergeInto(raw, spec.targetId);
      if (primaryTargetId) effectiveTargetId = primaryTargetId;
    }
    // B1 / #73 reverse merge (Action tier): the pseudo lives on the CHILD
    // Action; fold it UP into the picked primary. Capture the primary's label
    // (from its full target id in actionOptions) BEFORE resolveActionMergeInto
    // drops the picker field, for the optimistic "Merged → X" pill on the child.
    // Override the POST target to the primary so action_merge folds the source
    // (this child) INTO it. resolveActionMergeInto seeds the child's full target
    // id as the sole source, so the existing cross-Item re-parent block below
    // moves the child into the primary's Item when they differ.
    if (kind === "action_merge_into") {
      const primaryId = String(raw.merge_primary_action ?? "").trim();
      const opt = (spec.actionOptions ?? [])
        .find((a) => (a.target_id ?? a.ai_slug) === primaryId);
      if (opt) {
        reverseMergePrimaryLabel = [opt.display_number, opt.text]
          .filter(Boolean).join(" ");
      }
      const primaryTargetId = resolveActionMergeInto(raw, spec.targetId);
      if (primaryTargetId) effectiveTargetId = primaryTargetId;
    }
    // #75: decode the deep-move destination ("Category › Sub-category"). May
    // redirect a same-Category pick to item_move_to_subcategory (no Category
    // move) or, for a cross-Category sub-cat, keep the move + chain a grouping
    // step after it. Runs BEFORE alsoCloseItem (which reads wireKind).
    const currentThreadSlug = spec.targetId.match(/^thread:([^/]+)/)?.[1] ?? "";
    const deepMove = planDeepMoveDestination(raw, currentThreadSlug, wireKind);
    wireKind = deepMove.wireKind;
    // B10: capture the "Move & Close" intent before reshape strips it.
    // FormData only includes a checkbox when checked; gated to closeable
    // Items. On wireKind so the combined Move form's Category tier carries it
    // (resolveItemMoveKind already pruned also_close on the Sub-category tier).
    const alsoCloseItem = wireKind === "item_move_to_category"
      && "also_close" in raw && spec.itemCloseable !== false;
    // B8 cross-Item action_merge: the merge picker offers Actions from other
    // Items, encoded as their full target id. Re-parent each cross-Item
    // source into the target's Item (action_move_to_item) BEFORE the merge,
    // then hand the merge plain ai_slugs (all siblings now). Fired here —
    // before reshape — so the moves are submitted ahead of the merge (absorb
    // order = submit order) and reshape's slug-coercion never sees a target
    // id. Mirrors B10's two-event submit. Each move bubbles a feedback-
    // submitted so it's tracked + (B7) retractable; the move's origin
    // breadcrumb rides former_display_number into next week's render.
    const mergeOriginWis: string[] = [];
    // Gate on wireKind (not kind) so the B1 reverse merge (action_merge_into →
    // action_merge) runs this cross-Item re-parent too: its sole source is the
    // CHILD's full target id, and the destination Item is the PRIMARY's —
    // derived from effectiveTargetId (which the reverse-merge block overrode to
    // the primary; for a forward action_merge it equals spec.targetId, so the
    // behaviour is unchanged). When child and primary already share an Item the
    // wi === targetWi check skips the move, exactly as a same-Item forward merge.
    if (wireKind === "action_merge") {
      const targetWi = effectiveTargetId.match(/work_item:([^/]+)/)?.[1] ?? "";
      const rawSources = Array.isArray(raw.source_action_item_slugs)
        ? (raw.source_action_item_slugs as string[])
        : raw.source_action_item_slugs != null
          ? [String(raw.source_action_item_slugs)]
          : [];
      const aiSlugs: string[] = [];
      for (const val of rawSources) {
        const isTargetId = val.includes("action_item:");
        const ai = isTargetId
          ? (val.match(/action_item:([^/]+)/)?.[1] ?? "")
          : val;
        const wi = isTargetId
          ? (val.match(/work_item:([^/]+)/)?.[1] ?? targetWi)
          : targetWi;
        if (!ai) continue;
        aiSlugs.push(ai);
        if (wi && wi !== targetWi) {
          mergeOriginWis.push(wi);
          const opt = (spec.actionOptions ?? []).find((o) => o.target_id === val);
          const movePayload: Record<string, unknown> = {
            to_work_item_slug: targetWi,
          };
          if (opt?.display_number) {
            movePayload.former_display_number = opt.display_number;
          }
          const moveRes: { event_id: string } = inFixtureMode()
            ? { event_id: crypto.randomUUID() }
            : await postJson<{ event_id: string }>(
                `/feedback/${encodeURIComponent(spec.runId)}`,
                {
                  kind: "action_move_to_item",
                  target: { type: "action_item", id: val },
                  payload: movePayload,
                },
              );
          document.dispatchEvent(new CustomEvent("feedback-submitted", {
            detail: {
              event_id: moveRes.event_id, kind: "action_move_to_item",
              payload: movePayload, submitted_at: new Date().toISOString(),
              targetId: val, targetType: "action_item",
            },
          }));
        }
      }
      // The merge now operates on plain ai_slugs, all under the target Item.
      raw.source_action_item_slugs = aiSlugs;
    }
    // #63: the move/place pickers now submit the friendly label; map it back
    // to the stable target slug BEFORE reshape (which ensureSlug()s the value).
    resolvePickedTargetSlugs(raw, spec.threadOptions, spec.itemOptions);
    // #60 PR4-v2: label→slug mapping for the Merge-Into-Existing pickers.
    if (wireKind === "merge_into_existing") {
      resolveMergeTargetPicks(
        raw, spec.mergeThreadOptions, spec.mergeItemOptions,
        spec.mergeActionOptions,
      );
    }
    // #38: mint the new sub-category's slug + disambiguated display name from
    // the typed name, collision-suffixed against the parent Category's groups
    // (open + closed) PLUS this session's pending adds — all carried in
    // spec.subcategoryOptions, which briefings.ts feeds from the optimistically-
    // mutated payload. Done before reshape so its slugify fallback sees the
    // minted slug and passes through. A collision suffixes BOTH (slug `-N` +
    // name " N") so two same-named groups are visually distinct (feedback 3.4).
    if (wireKind === "subcategory_add" && typeof raw.name === "string") {
      const minted = mintSubcategory(
        raw.name as string,
        (spec.subcategoryOptions ?? []).map((s) => s.slug),
      );
      raw.subcategory_slug = minted.slug;
      raw.name = minted.name;
    }
    const payload = reshapePayloadForKind(wireKind, raw, spec.targetId);
    // A①: thread the merge target's label into the event so the optimistic
    // "Recently Merged → into X" can name it + survive reload. The absorber
    // ignores unknown payload keys, so this is harmless on the agent side.
    if (
      (kind === "work_item_merge" || kind === "action_merge")
      && spec.mergeTargetLabel
    ) {
      (payload as Record<string, unknown>).target_label = spec.mergeTargetLabel;
    }
    // #73 reverse merge (Item + B1 Action tier): the primary is the PICKED
    // entity, not spec's (the menu is open on the child). Use its captured label
    // so the child grays as "Merged → <primary>".
    if (
      (kind === "item_merge_into" || kind === "action_merge_into")
      && reverseMergePrimaryLabel
    ) {
      (payload as Record<string, unknown>).target_label = reverseMergePrimaryLabel;
    }
    // B8: thread the cross-Item sources' origin Items so the optimistic
    // action_merge can gray them in place (they live under their origin Item
    // until next run's re-parent applies). Absorber ignores the key. wireKind
    // catches the B1 reverse merge (action_merge_into → action_merge), whose
    // single cross-Item source is the child being folded up.
    if (wireKind === "action_merge" && mergeOriginWis.length) {
      (payload as Record<string, unknown>).merge_origin_work_item_slugs =
        [...new Set(mergeOriginWis)];
    }
    injectQuestionContext(
      payload as Record<string, unknown>, wireKind, spec.questionContext,
    );
    // A³/A² (2026-06-03): inject the bucket entry's source_ref + id for the
    // Place tiers (not visible form fields). place_from_unfiled keys the
    // entry as `unfiled_id`; the new tiers use `entry_id`.
    if (
      (wireKind === "place_from_unfiled" || wireKind === "place_as_category"
        || wireKind === "place_as_action")
      && spec.bucketContext
    ) {
      const p = payload as Record<string, unknown>;
      p.source_ref = spec.bucketContext.sourceRef;
      if (wireKind === "place_from_unfiled") {
        p.unfiled_id = spec.bucketContext.entryId;
      } else {
        p.entry_id = spec.bucketContext.entryId;
      }
      // #1.4: a candidate-folded row retires its inferred thread on placement.
      if (spec.bucketContext.candidateSlug) {
        p.candidate_slug = spec.bucketContext.candidateSlug;
      }
    }
    // #60 PR4-v2 merge_into_existing: inject the entry's identity + text
    // snapshot (source_ref drives next-run bucket suppression; text becomes
    // the born-closed see-also note's text agent-side; candidate_slug retires
    // a candidate-folded row's inferred thread).
    if (wireKind === "merge_into_existing" && spec.bucketContext) {
      const p = payload as Record<string, unknown>;
      p.source_ref = spec.bucketContext.sourceRef;
      p.entry_id = spec.bucketContext.entryId;
      if (spec.bucketContext.entryText) p.text = spec.bucketContext.entryText;
      if (spec.bucketContext.candidateSlug) {
        p.candidate_slug = spec.bucketContext.candidateSlug;
      }
    }
    // not_relevant on a bucket entry rejects its INGEST source (the absorber
    // reads source_ref for unfiled/informational targets); informational
    // acknowledge stores the source_ref for audit. Both inject from context.
    if (
      (wireKind === "not_relevant" || wireKind === "informational_acknowledge")
      && spec.bucketContext
    ) {
      (payload as Record<string, unknown>).source_ref = spec.bucketContext.sourceRef;
    }
    // Decision 3.6: thread the moving entity's former path into the move
    // event so next week's render can show the "(formerly N.M.K, ...)"
    // breadcrumb. The absorber ignores it on legacy/absent → no crumb.
    // On wireKind so the combined Move form's Category tier gets its crumb;
    // a Sub-category move deliberately carries NONE (pinned policy 5 —
    // grouping never renumbers, so there is nothing "former" to show).
    if (
      (wireKind === "item_move_to_category" || wireKind === "action_move_to_item")
      && spec.movingFromDisplayNumber
    ) {
      (payload as Record<string, unknown>).former_display_number =
        spec.movingFromDisplayNumber;
    }
    try {
      const targetType =
        (wireKind === "comment" || wireKind === "missing_item")
        && spec.targetType !== "thread"
          ? "section"
          : spec.targetType;
      // Fixture mode: there's no Worker to talk to (the static server
      // returns 501 for POST). Short-circuit to a fake event_id so the
      // SPA's optimistic Submitted-list update + lifecycle UX is
      // exercisable in /?fixture=2. Mirrors the fetchFeedback / sendReceipt
      // fixture-mode stubs in app.ts.
      const body: Record<string, unknown> = {
        kind: wireKind,
        // #73: effectiveTargetId is the picked primary for an item_merge_into
        // (reverse merge); spec.targetId for every other kind.
        target: { type: targetType, id: effectiveTargetId },
        payload,
      };
      // #74a: on a gated kind, hand the Worker the recipient's own in-session
      // events so the close-gate replays a just-POSTed move-out that KV's
      // eventually-consistent list() may not have indexed yet — fixes the
      // "can't close a sub-category I just emptied" blocker. Transport-only.
      if (GATE_HINT_KINDS.has(wireKind)) {
        body.client_events = gateHintEvents(spec.runId);
      }
      const res: { event_id: string } = inFixtureMode()
        ? { event_id: crypto.randomUUID() }
        : await postJson<{ event_id: string }>(
            `/feedback/${encodeURIComponent(spec.runId)}`,
            body,
          );
      status.textContent = "Submitted. Thanks.";
      submit.textContent = "Done";
      form.querySelectorAll("input, textarea").forEach((el) => {
        (el as HTMLInputElement).disabled = true;
      });
      const event: SubmittedEvent = {
        event_id: res.event_id,
        kind: wireKind,
        payload,
        submitted_at: new Date().toISOString(),
        // B7: a just-submitted event is by definition not yet absorbed, so
        // it's immediately retractable.
        pending: true,
      };
      // Real-time feedback #1 (Part A): bubble the submission up to
      // renderRun so it can fold the event into the SHARED `feedback`
      // array. Without this, the optimistic Submitted-list + pending
      // count live only in this control's local closure and get wiped
      // by the next viewer re-render (KIL toggle / compactness chevron),
      // which rebuilds every control from the stale fetched snapshot.
      // Carries `target` (the local SubmittedEvent omits it) because the
      // shared index keys on target id. Dispatched FIRST so a re-render
      // triggered by feedback-state-changed below sees the pushed event.
      document.dispatchEvent(
        new CustomEvent("feedback-submitted", {
          detail: {
            event_id: event.event_id,
            kind: wireKind,
            payload,
            submitted_at: event.submitted_at,
            // #73: a reverse merge is recorded against the picked primary.
            targetId: effectiveTargetId,
            targetType: spec.targetType,
          },
        }),
      );
      // KIL events drive optimistic SPA-side payload mutation —
      // dispatch a CustomEvent the viewer (app.ts::renderRun) listens
      // for and flips `kil_marked` on the in-memory record before
      // re-rendering. The Worker side already mutates the kil-marks
      // snapshot for the KIL tab (worker/src/kil_mutate.ts); this
      // bubbles the same intent to the briefing-row badge so the
      // operator sees the orange "KIL" chip appear/disappear in the
      // same session.
      if (
        kind === "item_kil_mark" || kind === "item_kil_unmark"
        || kind === "action_kil_mark" || kind === "action_kil_unmark"
      ) {
        document.dispatchEvent(
          new CustomEvent("kil-mark-changed", {
            detail: { kind, targetId: spec.targetId },
          }),
        );
      }
      // Real-time feedback #1 (Part B): lifecycle/priority kinds drive an
      // optimistic in-payload mutation (status / closure_candidate /
      // priority) the same way KIL does. renderRun applies it to the
      // in-memory record.payload and re-renders, so the Action/Item dims
      // + moves to Recently Closed, the "(Agent proposes closing)" suffix
      // clears, the Category gets its "Closed" pill, or the priority pill
      // re-colors — without waiting for the next cron.
      // On wireKind (not the pseudo): the combined Move form's Sub-category
      // tier resolves to item_move_to_subcategory, which IS optimistic — the
      // pseudo "item_move" never appears in OPTIMISTIC_STATE_KINDS. For every
      // non-pseudo kind wireKind === kind, so this is behavior-preserving.
      if (OPTIMISTIC_STATE_KINDS.includes(wireKind)) {
        document.dispatchEvent(
          new CustomEvent("feedback-state-changed", {
            // #73: effectiveTargetId = the picked primary for a reverse merge
            // (work_item_merge closes the source by payload slug, so the
            // applier is targetId-agnostic — this keeps the event coherent).
            detail: { kind: wireKind, targetId: effectiveTargetId, payload },
          }),
        );
      }
      // #75: a cross-Category deep move chains a grouping step — file the moved
      // Item into the destination Category's sub-category. POSTed after the move
      // so absorb order = submit order: the Item is in the destination Category
      // by the time the group move applies + is validated against it.
      if (deepMove.chainSubcategory) {
        const movedItemSlug = spec.targetId.match(/work_item:([^/]+)/)?.[1] ?? "";
        const groupTargetId =
          `thread:${deepMove.chainThread}/work_item:${movedItemSlug}`;
        const groupPayload: Record<string, unknown> = {
          to_subcategory_slug: deepMove.chainSubcategory,
        };
        const groupRes: { event_id: string } = inFixtureMode()
          ? { event_id: crypto.randomUUID() }
          : await postJson<{ event_id: string }>(
              `/feedback/${encodeURIComponent(spec.runId)}`,
              {
                kind: "item_move_to_subcategory",
                target: { type: "work_item", id: groupTargetId },
                payload: groupPayload,
                client_events: gateHintEvents(spec.runId),
              },
            );
        document.dispatchEvent(new CustomEvent("feedback-submitted", {
          detail: {
            event_id: groupRes.event_id, kind: "item_move_to_subcategory",
            payload: groupPayload, submitted_at: new Date().toISOString(),
            targetId: groupTargetId, targetType: "work_item",
          },
        }));
        document.dispatchEvent(new CustomEvent("feedback-state-changed", {
          detail: {
            kind: "item_move_to_subcategory", targetId: groupTargetId,
            payload: groupPayload,
          },
        }));
      }
      // B10: "Move & Close" — fire a second work_item_close after the move so
      // the (closeable, 0-open-Action) Item lands in the target Category's
      // Recently Closed. Same target; both events bubble + drive optimistics.
      if (alsoCloseItem) {
        const closePayload: Record<string, unknown> = {};
        const closeRes: { event_id: string } = inFixtureMode()
          ? { event_id: crypto.randomUUID() }
          : await postJson<{ event_id: string }>(
              `/feedback/${encodeURIComponent(spec.runId)}`,
              {
                kind: "work_item_close",
                target: { type: "work_item", id: spec.targetId },
                payload: closePayload,
                // #74a: the move was POSTed (and bubbled) just above; carry it
                // so this contingent close isn't blocked by KV list lag.
                client_events: gateHintEvents(spec.runId),
              },
            );
        document.dispatchEvent(new CustomEvent("feedback-submitted", {
          detail: {
            event_id: closeRes.event_id, kind: "work_item_close",
            payload: closePayload, submitted_at: new Date().toISOString(),
            targetId: spec.targetId, targetType: "work_item",
          },
        }));
        document.dispatchEvent(new CustomEvent("feedback-state-changed", {
          detail: {
            kind: "work_item_close", targetId: spec.targetId, payload: closePayload,
          },
        }));
      }
      // Cascade-close: the Item's own state-changed rode the generic
      // OPTIMISTIC dispatch above; now echo each child close so the Actions
      // gray into "Recently Closed Actions" in the same visual settle. Safe
      // here — every POST is done, so a re-render can no longer orphan an
      // in-flight loop.
      if (kind === "work_item_close_cascade") {
        const childReason = cascadeChildReason(spec.movingFromDisplayNumber);
        for (const id of cascadeClosedChildren) {
          document.dispatchEvent(new CustomEvent("feedback-state-changed", {
            detail: {
              kind: "action_close", targetId: id,
              payload: { reason: childReason },
            },
          }));
        }
      }
      // Swap the popover back to the picker (now leads with this new entry
      // in the Submitted list) after a brief pause so the user can read
      // the success message.
      setTimeout(() => onSuccess(event), 1200);
    } catch (err) {
      // N1 (2026-07-14, adversarial trace): the one-click Accept path renders this
      // form hidden and requestSubmit()s it. On a POST failure the error + retry
      // Submit below would otherwise be painted inside a display:none form — a
      // blank popover with the merge silently not applied (false "succeeded",
      // against the #118 no-false-success bar). Un-hide so the message + Submit
      // are visible and retryable. No-op for the normal (already-visible) path.
      form.hidden = false;
      submit.disabled = false;
      if (err instanceof ApiError) {
        const body = err.body as { message?: unknown } | null;
        const msg = body && typeof body.message === "string" ? body.message : "";
        status.textContent = msg || `Error: ${err.status}`;
      } else {
        status.textContent = "Network error.";
      }
    }
  });

  return form;
}
